import { Group, Pouch } from '../types';
import {
  GRID_SIZE,
  findInnermostContainerGroup,
  findPrimaryGroupIdForPouchCenter,
  generateRoundedRectPath,
  getPathBounds,
  isGroupPhysicallyContained,
  snapToGrid,
  translatePath,
} from './GroupLogic';

export const PITCH_X = 4 * GRID_SIZE;
export const PITCH_Y = 3 * GRID_SIZE;
export const STAGGER_X = 2 * GRID_SIZE;
export const CLUSTER_BUFFER_Y = 2 * GRID_SIZE;
export const CLUSTER_BUFFER_X = 0;
export const POUCH_RADIUS = 35;
export const POUCH_DIAMETER = POUCH_RADIUS * 2;
/** 計算エリア端・他グループ path との最小隙間（1 スナップ） */
export const PLACEMENT_GAP = GRID_SIZE;

/** GroupComponent.scss: border-top 10px */
const GROUP_BORDER_TOP_PX = 10;
/** GroupComponent.scss: .group-title-container left/top 0.7rem（16px 基準） */
const GROUP_TITLE_INSET_PX = 11;
/** GroupComponent.scss: .group-title-input min-height 1.4em × font 1.1rem 相当 */
const GROUP_TITLE_LINE_PX = 28;
/** GroupComponent.scss: .group-title-container max-width calc(100% - 1.4rem) 相当 */
const GROUP_TITLE_CONTAINER_MARGIN_PX = 28;
/** タイトル折り返し推定（半角幅 1 単位あたりの px） */
const GROUP_TITLE_PX_PER_HALF_UNIT = 7;
/** PouchComponent truncateText の maxWidth（半角幅単位） */
const POUCH_TITLE_MAX_WIDTH_UNITS = 140;
const POUCH_TITLE_PX_PER_HALF_UNIT = 7;

/** 配置限界の左上（ふうま path 実測: left 25 = member left 40 − inset 15）。これより左・上には置かない */
export const PLACEMENT_ORIGIN_LEFT = 25;
export const PLACEMENT_ORIGIN_TOP = 40;

/** @deprecated PLACEMENT_ORIGIN_TOP + 標準 insets で決まる。互換のため残す */
export const MIN_PLACEMENT_POUCH_CENTER_Y = 125;

/** GroupComponent 実測: ふうま path left 50 ← member left 65 の差 */
const GROUP_TITLE_CONTENT_INSET_LEFT_PX = 15;

export interface SplitGroupInsets {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface BrickLayoutOptions {
  pouchTitles?: (string | undefined)[];
  /** 配置するパウチと同順。×3 以上で行間・外接にレプリカ余白 */
  memberMultipliers?: number[];
}

/** 配置探索の列幅用ラベル（タイトル優先、なければ lastExpression） */
export function pouchPlacementDisplayLabel(pouch: {
  title?: string;
  lastExpression?: string;
}): string | undefined {
  const title = pouch.title?.trim();
  if (title) return title;
  const expr = pouch.lastExpression?.trim();
  return expr || undefined;
}

function rowReplicaPadPx(
  multipliers: number[] | undefined,
  row: number,
  cols: number,
  n: number,
): number {
  if (!multipliers?.length) return 0;
  const start = row * cols;
  const end = Math.min(start + cols, n);
  let pad = 0;
  for (let i = start; i < end; i++) {
    pad = Math.max(pad, replicaSubtotalBottomPadPx([multipliers[i] ?? 1]));
  }
  return pad;
}

/** PouchComponent と同じ半角幅カウント（全角 2・半角 1） */
export function measureTextWidthUnits(text: string): number {
  let width = 0;
  for (const char of text) {
    const code = char.charCodeAt(0);
    if (
      (code >= 0x0020 && code <= 0x007e) ||
      (code >= 0xff61 && code <= 0xff9f)
    ) {
      width += 1;
    } else {
      width += 2;
    }
  }
  return width;
}

function groupTitleFontScale(title: string): number {
  return title.length >= 30 ? 0.9 : 1.1;
}

/** グループ path 内のタイトル折り返し行数（配置用の概算） */
export function estimateGroupTitleLineCount(
  title: string,
  clusterMemberWidth: number,
): number {
  const trimmed = title.trim();
  if (!trimmed) return 0;
  const containerWidth = Math.max(
    80,
    clusterMemberWidth - GROUP_TITLE_CONTAINER_MARGIN_PX,
  );
  const pxPerUnit =
    GROUP_TITLE_PX_PER_HALF_UNIT * groupTitleFontScale(trimmed);
  let lines = 1;
  let currentLinePx = 0;
  for (const char of trimmed) {
    const code = char.charCodeAt(0);
    const units =
      (code >= 0x0020 && code <= 0x007e) ||
      (code >= 0xff61 && code <= 0xff9f)
        ? 1
        : 2;
    const charPx = units * pxPerUnit;
    if (currentLinePx + charPx > containerWidth && currentLinePx > 0) {
      lines += 1;
      currentLinePx = charPx;
    } else {
      currentLinePx += charPx;
    }
  }
  return lines;
}

function pouchTitleDisplayHalfWidthPx(title?: string): number {
  if (!title?.trim()) return POUCH_RADIUS;
  const units = Math.min(
    measureTextWidthUnits(title.trim()),
    POUCH_TITLE_MAX_WIDTH_UNITS,
  );
  return (units * POUCH_TITLE_PX_PER_HALF_UNIT) / 2;
}

function columnGapForPouchTitles(
  leftTitle?: string,
  rightTitle?: string,
): number {
  const needPx =
    pouchTitleDisplayHalfWidthPx(leftTitle) +
    pouchTitleDisplayHalfWidthPx(rightTitle) +
    PLACEMENT_GAP;
  return Math.max(PITCH_X, snapToGrid(needPx));
}

function relXForPouchIndex(
  index: number,
  cols: number,
  titles?: (string | undefined)[],
): number {
  const col = index % cols;
  const row = Math.floor(index / cols);
  let x = 0;
  for (let c = 0; c < col; c++) {
    const leftIdx = row * cols + c;
    const rightIdx = row * cols + c + 1;
    x += columnGapForPouchTitles(titles?.[leftIdx], titles?.[rightIdx]);
  }
  return x + (row % 2) * STAGGER_X;
}

function clusterMeetsPlacementOrigin(
  positions: { x: number; y: number }[],
  insets: SplitGroupInsets,
): boolean {
  const groupBounds = computeSplitGroupBounds(positions, insets);
  return (
    groupBounds.top >= PLACEMENT_ORIGIN_TOP &&
    groupBounds.left >= PLACEMENT_ORIGIN_LEFT
  );
}

/** グループタイトルの折り返し行数と横拡張（配置・insets 共通） */
export function resolveGroupTitleLayout(
  title: string,
  clusterMemberWidth: number,
): { lineCount: number; extraSide: number } {
  const trimmed = title.trim();
  if (!trimmed || clusterMemberWidth <= 0) {
    return { lineCount: 1, extraSide: 0 };
  }

  const oneLineWidthPx =
    measureTextWidthUnits(trimmed) *
    GROUP_TITLE_PX_PER_HALF_UNIT *
    groupTitleFontScale(trimmed);
  const containerWidth = clusterMemberWidth - GROUP_TITLE_CONTAINER_MARGIN_PX;

  if (oneLineWidthPx <= containerWidth) {
    return { lineCount: 1, extraSide: 0 };
  }

  let extraSide = 0;
  const neededMemberWidth = oneLineWidthPx + GROUP_TITLE_CONTAINER_MARGIN_PX;
  if (neededMemberWidth > clusterMemberWidth) {
    const widenEachSide = Math.ceil(
      (neededMemberWidth - clusterMemberWidth) / 2,
    );
    extraSide = snapToGrid(Math.max(GRID_SIZE, widenEachSide));
  }
  const effectiveContainer =
    clusterMemberWidth + extraSide * 2 - GROUP_TITLE_CONTAINER_MARGIN_PX;
  if (oneLineWidthPx <= effectiveContainer) {
    return { lineCount: 1, extraSide };
  }

  return {
    lineCount: Math.max(
      1,
      estimateGroupTitleLineCount(trimmed, clusterMemberWidth + extraSide * 2),
    ),
    extraSide,
  };
}

function computeGroupTitlePouchShiftY(
  title: string | undefined,
  clusterMemberWidth: number | undefined,
): number {
  const trimmed = title?.trim();
  if (!trimmed || clusterMemberWidth == null || clusterMemberWidth <= 0) {
    return 0;
  }
  const { lineCount } = resolveGroupTitleLayout(trimmed, clusterMemberWidth);
  if (lineCount <= 1) return 0;
  return snapToGrid((lineCount - 1) * GROUP_TITLE_LINE_PX);
}

/** レプリカ円表示と同じ等価乗数（PouchComponent.renderReplicaCircles） */
export function equivalentPositiveMultiplier(multiplier: number): number {
  if (multiplier > 0) return multiplier;
  const abs = Math.abs(multiplier);
  return abs > 0 ? 1 / abs : 1;
}

/** ×3 以上でレプリカが SUBTOTAL 帯にかぶる分。グループ path 下余白に 1 スナップ追加 */
export function replicaSubtotalBottomPadPx(multipliers: number[]): number {
  return multipliers.some(m => equivalentPositiveMultiplier(m) >= 3)
    ? GRID_SIZE
    : 0;
}

/** split 後グループ path の内側余白（タイトル・subtotal 帯を空ける。配置探索には使わない） */
export function computeSplitGroupInsets(
  hasTitle: boolean,
  title?: string,
  clusterMemberWidth?: number,
  extraBottom = 0,
): SplitGroupInsets {
  const side = 2 * GRID_SIZE;
  const leftBase = hasTitle ? GROUP_TITLE_CONTENT_INSET_LEFT_PX : GRID_SIZE;
  const bottom = 2 * GRID_SIZE + extraBottom;
  if (!hasTitle) {
    return { left: leftBase, top: side, right: side, bottom };
  }

  const singleLineTop =
    GROUP_BORDER_TOP_PX + GROUP_TITLE_INSET_PX + GROUP_TITLE_LINE_PX;
  let lineCount = 1;
  let extraSide = 0;
  const trimmed = title?.trim();

  if (trimmed && clusterMemberWidth != null && clusterMemberWidth > 0) {
    const layout = resolveGroupTitleLayout(trimmed, clusterMemberWidth);
    lineCount = layout.lineCount;
    extraSide = layout.extraSide;
  }

  const top = singleLineTop + (lineCount - 1) * GROUP_TITLE_LINE_PX;
  return {
    left: leftBase + extraSide,
    top: snapToGrid(top),
    right: side + extraSide,
    bottom,
  };
}

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface Viewport extends Rect {}

export interface SplitPlacementPlan {
  positions: { x: number; y: number }[];
  pouches: Pouch[];
  groups: Group[];
  /** 新規 Group 作成時の path bounds。createNewGroup=false でも参考値として入る */
  splitGroupBounds: Rect;
  createNewGroup: boolean;
  parentGroupId?: string;
}

export type SplitInGroupMode = 'ungrouped' | 'sole-member' | 'multi-member';

export interface SplitInGroupContext {
  mode: SplitInGroupMode;
  createNewGroup: boolean;
  excludeGroupId?: string;
  parentGroupId?: string;
  parentBounds?: Rect;
  /** group path 重なり判定から除外（Case A: 親＋祖先） */
  overlapExcludeGroupIds?: string[];
  /** 重なってはいけない group（Case B: 直接の親） */
  mustAvoidGroupIds?: string[];
}

export function resolveSplitInGroupContext(
  sourcePouch: Pouch,
  groups: Group[],
  pouches: Pouch[] = []
): SplitInGroupContext {
  const sourceGroup = findGroupForPouchId(sourcePouch.id, groups);
  if (!sourceGroup) {
    return { mode: 'ungrouped', createNewGroup: true };
  }
  const siblingCount = sourceGroup.pouches.filter(id => id !== sourcePouch.id).length;
  let parentBounds = getGroupPathBounds(sourceGroup) ?? undefined;
  if (!parentBounds) {
    parentBounds = boundsFromGroupMemberPouches(sourceGroup, pouches) ?? undefined;
  }
  if (siblingCount === 0) {
    const overlapExcludeGroupIds = collectSoleMemberOverlapExcludeGroupIds(
      sourceGroup.id,
      sourcePouch,
      groups
    );
    return {
      mode: 'sole-member',
      createNewGroup: false,
      excludeGroupId: sourceGroup.id,
      parentGroupId: sourceGroup.id,
      parentBounds,
      overlapExcludeGroupIds,
    };
  }
  return {
    mode: 'multi-member',
    createNewGroup: true,
    parentGroupId: sourceGroup.id,
    parentBounds,
    overlapExcludeGroupIds: [],
    mustAvoidGroupIds: [sourceGroup.id],
  };
}

/** Case A: 直接親を内包する最も外側の祖先 bounds（外包）。探索・収まり判定に使用 */
export function resolveSoleMemberPlacementEnvelope(
  parentGroupId: string,
  directParentBounds: Rect,
  groups: Group[]
): Rect {
  const ancestorIds = groupsPhysicallyContaining(parentGroupId, groups);
  let envelope = directParentBounds;
  for (const aid of ancestorIds) {
    const g = groups.find(x => x.id === aid);
    const b = g ? getGroupPathBounds(g) : null;
    if (!b || !groupBoundsStrictlyInside(directParentBounds, b)) continue;
    const envArea =
      Math.max(0, envelope.right - envelope.left) *
      Math.max(0, envelope.bottom - envelope.top);
    const bArea =
      Math.max(0, b.right - b.left) * Math.max(0, b.bottom - b.top);
    if (bArea >= envArea) {
      envelope = b;
    }
  }
  return envelope;
}

/** パウチ中心が path bounds 内にあるグループ（外包判定用） */
function groupsWhoseBoundsContainPoint(
  x: number,
  y: number,
  groups: Group[]
): string[] {
  return groups
    .filter(g => {
      const b = getGroupPathBounds(g);
      if (!b) return false;
      return x >= b.left && x <= b.right && y >= b.top && y <= b.bottom;
    })
    .map(g => g.id);
}

/** Case A: 分解中ずっと group path 重なりから除外する ID 集合 */
export function collectSoleMemberOverlapExcludeGroupIds(
  parentGroupId: string,
  sourcePouch: Pouch,
  groups: Group[]
): string[] {
  const ids = new Set<string>([
    parentGroupId,
    ...groupsPhysicallyContaining(parentGroupId, groups),
    ...groupsWhoseBoundsContainPoint(sourcePouch.x, sourcePouch.y, groups),
  ]);
  return Array.from(ids);
}

function groupBoundsStrictlyInside(inner: Rect, outer: Rect): boolean {
  return (
    inner.left > outer.left &&
    inner.right < outer.right &&
    inner.top > outer.top &&
    inner.bottom < outer.bottom
  );
}

/** 物理的に groupId を内包するグループ ID（祖先） */
export function groupsPhysicallyContaining(
  groupId: string,
  groups: Group[]
): string[] {
  const innerGroup = groups.find(g => g.id === groupId);
  const innerBounds = innerGroup ? getGroupPathBounds(innerGroup) : null;
  if (!innerBounds) {
    return groups
      .filter(
        g => g.id !== groupId && isGroupPhysicallyContained(groupId, g.id, groups)
      )
      .map(g => g.id);
  }
  return groups
    .filter(g => {
      if (g.id === groupId) return false;
      const outerBounds = getGroupPathBounds(g);
      if (!outerBounds) return false;
      return groupBoundsStrictlyInside(innerBounds, outerBounds);
    })
    .map(g => g.id);
}

function approximatePathBounds(path: string): Rect | null {
  const nums = path.match(/-?\d+(?:\.\d+)?/g);
  if (!nums || nums.length < 4) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i + 1 < nums.length; i += 2) {
    const x = parseFloat(nums[i]);
    const y = parseFloat(nums[i + 1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  if (!Number.isFinite(minX)) return null;
  return { left: minX, top: minY, right: maxX, bottom: maxY };
}

/** getPathBounds の DOM 非依存フォールバック付き */
export function getGroupPathBounds(group: Group): Rect | null {
  const fromDom = getPathBounds(group.path);
  if (fromDom) return fromDom;
  return approximatePathBounds(group.path);
}

/** path bounds 未取得時: メンバー パウチ外接から親 bounds を推定 */
function boundsFromGroupMemberPouches(group: Group, pouches: Pouch[]): Rect | null {
  const memberIds = new Set(group.pouches ?? []);
  let bounds: Rect | null = null;
  for (const p of pouches) {
    if (!memberIds.has(p.id)) continue;
    if ((p as { _isDeleting?: boolean })._isDeleting) continue;
    bounds = mergeRects(bounds, pouchContentRect(p));
  }
  return bounds;
}

/** split 配置の障害物（全 group path + 他パウチ）の右下 extent */
function computeSplitObstacleExtent(
  pouches: Pouch[],
  groups: Group[],
  excludePouchId?: string
): { right: number; bottom: number } {
  let right = 0;
  let bottom = 0;
  for (const g of groups) {
    const b = getGroupPathBounds(g);
    if (b) {
      right = Math.max(right, b.right);
      bottom = Math.max(bottom, b.bottom);
    }
  }
  for (const p of pouches) {
    if (p.id === excludePouchId) continue;
    if ((p as { _isDeleting?: boolean })._isDeleting) continue;
    right = Math.max(right, p.x + POUCH_RADIUS);
    bottom = Math.max(bottom, p.y + POUCH_RADIUS);
  }
  return { right, bottom };
}

function positionsFitInRect(
  positions: { x: number; y: number }[],
  rect: Rect
): boolean {
  for (const p of positions) {
    const b = pouchCircleBounds(p);
    if (
      b.left < rect.left ||
      b.top < rect.top ||
      b.right > rect.right ||
      b.bottom > rect.bottom
    ) {
      return false;
    }
  }
  return true;
}

/** Case A: 分解クラスター（メンバー + グループ UI 余白）が親 path 内に収まるか */
function splitClusterFitsInParentBounds(
  positions: { x: number; y: number }[],
  parentBounds: Rect,
  insets: SplitGroupInsets
): boolean {
  const groupBounds = computeSplitGroupBounds(positions, insets);
  return (
    groupBounds.left >= parentBounds.left &&
    groupBounds.top >= parentBounds.top &&
    groupBounds.right <= parentBounds.right &&
    groupBounds.bottom <= parentBounds.bottom
  );
}

/** Case A: 親内 top-left 配置（全方位・最短距離、padding 込みで収まる場合のみ） */
function trySoleMemberTopLeftPlacement(
  n: number,
  sourceX: number,
  sourceY: number,
  pouches: Pouch[],
  groups: Group[],
  parentBounds: Rect,
  placementViewport: Viewport,
  searchViewport: Viewport,
  placementOptions: ClusterPlacementOptions,
  searchInsets: SplitGroupInsets,
  displayInsets: SplitGroupInsets
): { x: number; y: number }[] | null {
  const slot = findClusterNearestTopLeftSlot(
    n,
    sourceX,
    sourceY,
    pouches,
    groups,
    placementViewport,
    placementOptions,
    searchInsets,
    searchViewport,
    displayInsets,
    parentBounds
  );
  if (!slot) return null;
  const positions = computeBrickLayout(n, slot.baseX, slot.baseY, 'topleft');
  if (!splitClusterFitsInParentBounds(positions, parentBounds, displayInsets)) {
    return null;
  }
  return positions;
}

function intersectRects(a: Rect, b: Rect): Rect | null {
  const left = Math.max(a.left, b.left);
  const top = Math.max(a.top, b.top);
  const right = Math.min(a.right, b.right);
  const bottom = Math.min(b.bottom, b.bottom);
  if (left >= right || top >= bottom) return null;
  return { left, top, right, bottom };
}

function clipViewportToRect(viewport: Viewport, clip: Rect): Viewport | null {
  return intersectRects(viewport, clip);
}

export function computeNearSquareGrid(n: number): { cols: number; rows: number } {
  const cols = Math.ceil(Math.sqrt(n));
  const rows = Math.ceil(n / cols);
  return { cols, rows };
}

/** パウチ stored (x,y) は PouchComponent の translate(-50%,-50%) により中心座標 */
function pouchCircleBounds(p: { x: number; y: number }): Rect {
  return {
    left: p.x - POUCH_RADIUS,
    top: p.y - POUCH_RADIUS,
    right: p.x + POUCH_RADIUS,
    bottom: p.y + POUCH_RADIUS,
  };
}

export function computeClusterBounds(
  positions: { x: number; y: number }[],
  bufferY = CLUSTER_BUFFER_Y,
  bufferX = CLUSTER_BUFFER_X
): Rect {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const p of positions) {
    const b = pouchCircleBounds(p);
    left = Math.min(left, b.left - bufferX);
    top = Math.min(top, b.top);
    right = Math.max(right, b.right + bufferX);
    bottom = Math.max(bottom, b.bottom + bufferY);
  }
  if (!Number.isFinite(left)) {
    return { left: 0, top: 0, right: 0, bottom: 0 };
  }
  return { left, top, right, bottom };
}

/** 塊内レイアウト相対座標（先頭セル左上基準）→ 円外接矩形の中心 */
function clusterCenterFromRelTopLeft(rel: { x: number; y: number }[]): { cx: number; cy: number } {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const p of rel) {
    left = Math.min(left, p.x);
    top = Math.min(top, p.y);
    right = Math.max(right, p.x + POUCH_DIAMETER);
    bottom = Math.max(bottom, p.y + POUCH_DIAMETER);
  }
  if (!Number.isFinite(left)) {
    return { cx: 0, cy: 0 };
  }
  return {
    cx: (left + right) / 2,
    cy: (top + bottom) / 2,
  };
}

function relTopLeftToCenterPositions(
  rel: { x: number; y: number }[],
  offsetX: number,
  offsetY: number,
  snapOffset = false
): { x: number; y: number }[] {
  const ox = snapOffset ? snapToGrid(offsetX) : offsetX;
  const oy = snapOffset ? snapToGrid(offsetY) : offsetY;
  // 返却 (x,y) はパウチ中心。ドラッグ等と同様 GRID_SIZE へスナップ（§2.2）
  return rel.map(p => ({
    x: snapToGrid(p.x + POUCH_RADIUS + ox),
    y: snapToGrid(p.y + POUCH_RADIUS + oy),
  }));
}

/** center アンカー配置のメンバー外接（左端・上端はここを基準に南東探索する） */
function clusterMemberBoundsAtCenter(
  n: number,
  centerX: number,
  centerY: number
): Rect {
  return computeClusterBounds(computeBrickLayout(n, centerX, centerY, 'center'), 0, 0);
}

/**
 * §2.2 互い違いレイアウト。
 * 返却 (x,y) はパウチ中心座標。
 * anchor=topleft のとき anchorX/Y は先頭セル左上、center のとき anchorX/Y は塊外接矩形の中心。
 */
export function computeBrickLayout(
  n: number,
  anchorX: number,
  anchorY: number,
  anchor: 'topleft' | 'center' = 'topleft',
  options?: BrickLayoutOptions,
): { x: number; y: number }[] {
  const { cols } = computeNearSquareGrid(n);
  const multipliers = options?.memberMultipliers;
  const rel: { x: number; y: number }[] = [];
  let rowExtraY = 0;
  for (let i = 0; i < n; i++) {
    const row = Math.floor(i / cols);
    if (row > 0 && i % cols === 0) {
      rowExtraY += rowReplicaPadPx(multipliers, row - 1, cols, n);
    }
    const relX = relXForPouchIndex(i, cols, options?.pouchTitles);
    const relY = row * PITCH_Y + rowExtraY;
    rel.push({ x: relX, y: relY });
  }

  if (anchor === 'topleft') {
    return relTopLeftToCenterPositions(
      rel,
      snapToGrid(anchorX),
      snapToGrid(anchorY),
      false
    );
  }

  const { cx: clusterCx, cy: clusterCy } = clusterCenterFromRelTopLeft(rel);
  return relTopLeftToCenterPositions(rel, anchorX - clusterCx, anchorY - clusterCy, true);
}

function rectsIntersect(a: Rect, b: Rect): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/** gap 未満の隙間も重なり扱い（隣接 1 スナップ空け用） */
function rectsCloserThanGap(a: Rect, b: Rect, gap: number): boolean {
  return (
    a.left < b.right + gap &&
    a.right + gap > b.left &&
    a.top < b.bottom + gap &&
    a.bottom + gap > b.top
  );
}

function isRectWithinViewportWithGap(rect: Rect, viewport: Viewport, gap: number): boolean {
  return (
    rect.left >= viewport.left + gap &&
    rect.top >= viewport.top + gap &&
    rect.right <= viewport.right - gap &&
    rect.bottom <= viewport.bottom - gap
  );
}

function unionRects(a: Rect, b: Rect): Rect {
  return {
    left: Math.min(a.left, b.left),
    top: Math.min(a.top, b.top),
    right: Math.max(a.right, b.right),
    bottom: Math.max(a.bottom, b.bottom),
  };
}

function translateRect(r: Rect, dx: number, dy: number): Rect {
  return {
    left: r.left + dx,
    top: r.top + dy,
    right: r.right + dx,
    bottom: r.bottom + dy,
  };
}

function circlesOverlap(
  a: { x: number; y: number },
  b: { x: number; y: number }
): boolean {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const minDist = POUCH_DIAMETER - 2;
  return dx * dx + dy * dy < minDist * minDist;
}

function isWithinViewport(positions: { x: number; y: number }[], viewport: Viewport): boolean {
  for (const p of positions) {
    const b = pouchCircleBounds(p);
    if (
      b.left < viewport.left ||
      b.top < viewport.top ||
      b.right > viewport.right ||
      b.bottom > viewport.bottom
    ) {
      return false;
    }
  }
  return true;
}

function findGroupForPouchId(
  pouchId: string,
  groups: Group[],
  pouches?: Pouch[],
): Group | undefined {
  const p = pouches?.find(x => x.id === pouchId);
  if (
    p &&
    typeof p.x === 'number' &&
    typeof p.y === 'number' &&
    !p._isDeleting
  ) {
    const primaryId = findPrimaryGroupIdForPouchCenter(p.x, p.y, groups);
    if (primaryId) {
      const primary = groups.find(g => g.id === primaryId);
      if (primary?.pouches?.includes(pouchId)) return primary;
    }
  }
  const members = groups.filter(g => g.pouches?.includes(pouchId));
  if (!members.length) return undefined;
  if (members.length === 1) return members[0];
  let best = members[0];
  let bestArea = Infinity;
  for (const g of members) {
    const b = getGroupPathBounds(g);
    if (!b) continue;
    const area = (b.right - b.left) * (b.bottom - b.top);
    if (area < bestArea) {
      bestArea = area;
      best = g;
    }
  }
  return best;
}

function pouchIdsInDisplacedBlock(
  blockIds: string[],
  groups: Group[],
): Set<string> {
  const ids = new Set<string>();
  for (const blockId of blockIds) {
    for (const gid of [
      blockId,
      ...groupsPhysicallyNestedInside(blockId, groups),
    ]) {
      const g = groups.find(x => x.id === gid);
      for (const pid of g?.pouches ?? []) {
        ids.add(pid);
      }
    }
  }
  return ids;
}

function clusterOverlapsOtherGroups(
  clusterBounds: Rect,
  groups: Group[],
  excludeGroupIds?: string | string[],
  gap = 0
): boolean {
  const excluded = new Set(
    excludeGroupIds === undefined
      ? []
      : Array.isArray(excludeGroupIds)
        ? excludeGroupIds
        : [excludeGroupIds]
  );
  for (const g of groups) {
    if (excluded.has(g.id)) continue;
    const gb = getGroupPathBounds(g);
    if (gb && rectsCloserThanGap(clusterBounds, gb, gap)) {
      return true;
    }
  }
  return false;
}

function clusterOverlapsAnyGroup(
  clusterBounds: Rect,
  groups: Group[],
  groupIds: string[],
  gap = 0
): boolean {
  for (const id of groupIds) {
    const g = groups.find(item => item.id === id);
    if (!g) continue;
    const gb = getGroupPathBounds(g);
    if (gb && rectsCloserThanGap(clusterBounds, gb, gap)) {
      return true;
    }
  }
  return false;
}

function rectsOverlapOccupiedRegions(
  rect: Rect,
  groups: Group[],
  pouches: Pouch[],
  options?: { excludePouchId?: string; excludeGroupId?: string }
): boolean {
  if (clusterOverlapsOtherGroups(rect, groups, options?.excludeGroupId)) {
    return true;
  }
  for (const existing of pouches) {
    if (existing.id === options?.excludePouchId) continue;
    if ((existing as { _isDeleting?: boolean })._isDeleting) continue;
    if (rectsIntersect(pouchCircleBounds(existing), rect)) {
      return true;
    }
  }
  return false;
}


/** split 後グループの path 外接（メンバー中心座標 + グループ UI 余白） */
export function computeSplitGroupBounds(
  positions: { x: number; y: number }[],
  insets: SplitGroupInsets = computeSplitGroupInsets(false),
): Rect {
  const memberBounds = computeClusterBounds(positions, 0, 0);
  return {
    left: memberBounds.left - insets.left,
    top: memberBounds.top - insets.top,
    right: memberBounds.right + insets.right,
    bottom: memberBounds.bottom + insets.bottom,
  };
}

/** §2.4 試行1: 塊全体が収まるか */
export function canPlaceClusterAt(
  positions: { x: number; y: number }[],
  pouches: Pouch[],
  groups: Group[],
  viewport: Viewport,
  options?: {
    excludePouchId?: string;
    excludeGroupId?: string;
    additionalBlockedRects?: Rect[];
  }
): boolean {
  const excludePouchId = options?.excludePouchId;
  const excludeGroupId = options?.excludeGroupId;

  if (!isWithinViewport(positions, viewport)) {
    return false;
  }

  for (const pos of positions) {
    for (const existing of pouches) {
      if (existing.id === excludePouchId) continue;
      if ((existing as { _isDeleting?: boolean })._isDeleting) continue;
      if (circlesOverlap(pos, existing)) {
        return false;
      }
    }
  }

  const clusterBounds = computeClusterBounds(positions);
  if (clusterOverlapsOtherGroups(clusterBounds, groups, excludeGroupId)) {
    return false;
  }

  const extraRects = options?.additionalBlockedRects ?? [];
  for (const rect of extraRects) {
    if (rectsOverlapOccupiedRegions(rect, groups, pouches, {
      excludePouchId,
      excludeGroupId,
    })) {
      return false;
    }
  }

  return true;
}

type ClusterPlacementOptions = {
  excludePouchId?: string;
  excludeGroupIds?: string[];
  mustAvoidGroupIds?: string[];
  /** Case A: 外包 envelope 内に収める（canvas viewport とは別判定） */
  placementEnvelopeClip?: Rect;
  /** Case A phase2: 外包 envelope 内に収まる配置は拒否（祖先の外へ） */
  mustNotFitInEnvelope?: Rect;
  pouchTitles?: (string | undefined)[];
};

type SoleMemberSplitSearchPhase = {
  envelopeClip?: Rect;
  mustNotFitInEnvelope?: Rect;
  excludeGroupIds: string[];
  searchClip?: Rect;
  placedOutsideEnvelope: boolean;
};

function buildSoleMemberSplitSearchPhases(
  parentGroupId: string,
  parentBounds: Rect,
  groups: Group[],
  frozenOverlapExcludeGroupIds: string[]
): SoleMemberSplitSearchPhase[] {
  const envelope = resolveSoleMemberPlacementEnvelope(
    parentGroupId,
    parentBounds,
    groups
  );
  const hasOuterAncestor =
    groupsPhysicallyContaining(parentGroupId, groups).length > 0;
  const phases: SoleMemberSplitSearchPhase[] = [
    {
      envelopeClip: envelope,
      searchClip: envelope,
      excludeGroupIds: frozenOverlapExcludeGroupIds,
      placedOutsideEnvelope: false,
    },
  ];
  if (hasOuterAncestor) {
    phases.push({
      mustNotFitInEnvelope: envelope,
      excludeGroupIds: [parentGroupId],
      placedOutsideEnvelope: true,
    });
  }
  return phases;
}

/** topleft (0,0) 基準のパウチ塊外接（配置走査用。グループ UI 余白は含めない） */
function clusterBoundsAtTopLeftOrigin(n: number): Rect {
  return computeClusterBounds(computeBrickLayout(n, 0, 0, 'topleft'), 0, 0);
}

/** center (0,0) 基準のパウチ塊外接（配置走査用。グループ UI 余白は含めない） */
function clusterBoundsAtCenterOrigin(n: number): Rect {
  return computeClusterBounds(computeBrickLayout(n, 0, 0, 'center'), 0, 0);
}

/**
 * 南東探索の上限だけ広げる（left/top は計算エリア端のストッパーを維持）
 * insets は配置探索用（タイトル帯なし）を渡すこと。
 */
export function expandViewportSearchExtents(
  placementViewport: Viewport,
  sourcePouch: Pouch,
  n: number,
  searchInsets: SplitGroupInsets
): Viewport {
  const clusterAtOrigin = clusterBoundsAtCenterOrigin(n);
  const clusterW = clusterAtOrigin.right - clusterAtOrigin.left;
  const clusterH = clusterAtOrigin.bottom - clusterAtOrigin.top;
  const atSource = computeSplitGroupBounds(
    computeBrickLayout(n, sourcePouch.x, sourcePouch.y, 'center'),
    searchInsets
  );
  return {
    left: placementViewport.left,
    top: placementViewport.top,
    right: Math.max(
      placementViewport.right,
      atSource.right,
      sourcePouch.x + clusterW
    ),
    bottom: Math.max(
      placementViewport.bottom,
      atSource.bottom,
      sourcePouch.y + clusterH
    ),
  };
}

/** split 用: コンテンツ bounds に N 個クラスターが収まる最小 right/bottom を保証 */
export function expandPlacementViewportForSplit(
  placementViewport: Viewport,
  sourcePouch: Pouch,
  n: number,
  searchInsets: SplitGroupInsets
): Viewport {
  const gap = PLACEMENT_GAP;
  const atOrigin = computeSplitGroupBounds(
    computeBrickLayout(n, 0, 0, 'topleft'),
    searchInsets
  );
  const atSource = computeSplitGroupBounds(
    computeBrickLayout(n, sourcePouch.x, sourcePouch.y, 'center'),
    searchInsets
  );
  return {
    left: placementViewport.left,
    top: placementViewport.top,
    right: Math.max(
      placementViewport.right,
      atOrigin.right + gap,
      atSource.right + gap
    ),
    bottom: Math.max(
      placementViewport.bottom,
      atOrigin.bottom + gap,
      atSource.bottom + gap
    ),
  };
}

/**
 * 大 N split: クラスター幅が探索帯より広いと first-fit 不可。
 * 親 bounds の東／南側に塊全体が収まるよう placement / search viewport を拡張する。
 */
export function ensureSplitSearchViewportForCluster(
  placementViewport: Viewport,
  searchViewport: Viewport,
  n: number,
  clusterInsets: SplitGroupInsets,
  options: {
    parentBounds?: Rect | null;
    sourcePouch?: Pouch;
    mustAvoidGroupIds?: string[];
    groups?: Group[];
    pouches?: Pouch[];
    extraMargin?: number;
  } = {}
): { placementViewport: Viewport; searchViewport: Viewport } {
  const clusterAtOrigin = computeSplitGroupBounds(
    computeBrickLayout(n, 0, 0, 'topleft'),
    clusterInsets
  );
  const clusterW = clusterAtOrigin.right - clusterAtOrigin.left;
  const clusterH = clusterAtOrigin.bottom - clusterAtOrigin.top;
  const gap = PLACEMENT_GAP;
  const extra =
    options.extraMargin ?? Math.max(480, clusterW, clusterH, n > 36 ? 2000 : 0);

  let minRight = Math.max(
    placementViewport.right,
    placementViewport.left + clusterW + gap * 2
  );
  let minBottom = Math.max(
    placementViewport.bottom,
    placementViewport.top + clusterH + gap * 2
  );

  const avoidBounds: Rect[] = [];
  if (options.parentBounds) {
    avoidBounds.push(options.parentBounds);
  }
  if (options.mustAvoidGroupIds && options.groups) {
    for (const gid of options.mustAvoidGroupIds) {
      const g = options.groups.find(x => x.id === gid);
      const gb = g ? getGroupPathBounds(g) : null;
      if (gb) avoidBounds.push(gb);
    }
  }
  for (const pb of avoidBounds) {
    minRight = Math.max(minRight, pb.right + clusterW + gap);
    minBottom = Math.max(minBottom, pb.bottom + clusterH + gap);
  }

  if (options.pouches && options.groups) {
    const ext = computeSplitObstacleExtent(
      options.pouches,
      options.groups,
      options.sourcePouch?.id
    );
    minRight = Math.max(minRight, ext.right + clusterW + gap);
    minBottom = Math.max(minBottom, ext.bottom + clusterH + gap);
  }

  if (options.sourcePouch) {
    const s = options.sourcePouch;
    minRight = Math.max(minRight, s.x + clusterW + gap);
    minBottom = Math.max(minBottom, s.y + clusterH + gap);
  }

  const placement = {
    ...placementViewport,
    right: Math.max(placementViewport.right, minRight),
    bottom: Math.max(placementViewport.bottom, minBottom),
  };
  const search = {
    ...searchViewport,
    right: Math.max(searchViewport.right, minRight + extra),
    bottom: Math.max(searchViewport.bottom, minBottom + extra),
  };
  return { placementViewport: placement, searchViewport: search };
}

/** §2.1 / §2.4 試行3: top-left 配置の探索範囲 */
function topLeftSlotViewportLimits(
  n: number,
  placementViewport: Viewport,
  searchViewport: Viewport,
  groupOverlapInsets: SplitGroupInsets,
  searchClip?: Rect,
  layoutOptions?: BrickLayoutOptions,
): { minBaseX: number; minBaseY: number; maxBaseX: number; maxBaseY: number } | null {
  const groupAtOrigin = computeSplitGroupBounds(
    computeBrickLayout(n, 0, 0, 'topleft', layoutOptions),
    groupOverlapInsets
  );
  const gap = PLACEMENT_GAP;
  let minBaseX = snapToGrid(placementViewport.left - groupAtOrigin.left);
  let minBaseY = snapToGrid(placementViewport.top - groupAtOrigin.top);
  let maxBaseX = searchViewport.right - gap - groupAtOrigin.right;
  let maxBaseY = searchViewport.bottom - gap - groupAtOrigin.bottom;

  if (searchClip) {
    minBaseX = Math.max(minBaseX, searchClip.left - groupAtOrigin.left);
    minBaseY = Math.max(minBaseY, searchClip.top - groupAtOrigin.top);
    maxBaseX = Math.min(maxBaseX, searchClip.right - gap - groupAtOrigin.right);
    maxBaseY = Math.min(maxBaseY, searchClip.bottom - gap - groupAtOrigin.bottom);
  }

  if (minBaseX > maxBaseX || minBaseY > maxBaseY) {
    return null;
  }
  return { minBaseX, minBaseY, maxBaseX, maxBaseY };
}

/** 全方位: anchor から Manhattan 距離の近い順に grid を走査 */
function findNearestOnGrid(
  anchorX: number,
  anchorY: number,
  minX: number,
  maxX: number,
  minY: number,
  maxY: number,
  tryAt: (x: number, y: number) => boolean
): { x: number; y: number } | null {
  const ax = snapToGrid(anchorX);
  const ay = snapToGrid(anchorY);
  if (ax >= minX && ax <= maxX && ay >= minY && ay <= maxY && tryAt(ax, ay)) {
    return { x: ax, y: ay };
  }
  const maxRing = Math.max(
    Math.ceil((ax - minX) / GRID_SIZE),
    Math.ceil((maxX - ax) / GRID_SIZE),
    Math.ceil((ay - minY) / GRID_SIZE),
    Math.ceil((maxY - ay) / GRID_SIZE)
  );
  for (let ring = 1; ring <= maxRing; ring++) {
    const offsets: { dx: number; dy: number }[] = [];
    for (let dy = -ring; dy <= ring; dy++) {
      for (let dx = -ring; dx <= ring; dx++) {
        if (Math.abs(dx) + Math.abs(dy) !== ring) continue;
        offsets.push({ dx, dy });
      }
    }
    offsets.sort((a, b) => {
      if (a.dy !== b.dy) return a.dy - b.dy;
      return a.dx - b.dx;
    });
    for (const { dx, dy } of offsets) {
      const x = ax + dx * GRID_SIZE;
      const y = ay + dy * GRID_SIZE;
      if (x < minX || x > maxX || y < minY || y > maxY) continue;
      if (tryAt(x, y)) return { x, y };
    }
  }
  return null;
}

/** §2.1 / §2.4 試行3: 塊ごと first-fit（上→下、左→右） */
export function findClusterTopLeftSlot(
  n: number,
  pouches: Pouch[],
  groups: Group[],
  placementViewport: Viewport,
  options?: ClusterPlacementOptions,
  searchInsets: SplitGroupInsets = computeSplitGroupInsets(false),
  searchViewport: Viewport = placementViewport,
  groupOverlapInsets: SplitGroupInsets = searchInsets,
  searchClip?: Rect
): { baseX: number; baseY: number } | null {
  const limits = topLeftSlotViewportLimits(
    n,
    placementViewport,
    searchViewport,
    groupOverlapInsets,
    searchClip,
    options?.pouchTitles ? { pouchTitles: options.pouchTitles } : undefined,
  );
  if (!limits) return null;
  const { minBaseX, minBaseY, maxBaseX, maxBaseY } = limits;
  const layoutOptions = options?.pouchTitles
    ? { pouchTitles: options.pouchTitles }
    : undefined;

  for (let y = minBaseY; y <= maxBaseY; y += GRID_SIZE) {
    for (let x = minBaseX; x <= maxBaseX; x += GRID_SIZE) {
      const positions = computeBrickLayout(n, x, y, 'topleft', layoutOptions);
      if (
        checkSplitClusterPositions(
          positions,
          pouches,
          groups,
          placementViewport,
          options,
          searchInsets,
          groupOverlapInsets
        ) === null
      ) {
        return { baseX: x, baseY: y };
      }
    }
  }
  return null;
}

/** §2.4: 元位置から全方位・最短 Manhattan で top-left 塊を探索 */
export function findClusterNearestTopLeftSlot(
  n: number,
  sourceX: number,
  sourceY: number,
  pouches: Pouch[],
  groups: Group[],
  placementViewport: Viewport,
  options?: ClusterPlacementOptions,
  searchInsets: SplitGroupInsets = computeSplitGroupInsets(false),
  searchViewport: Viewport = placementViewport,
  groupOverlapInsets: SplitGroupInsets = searchInsets,
  searchClip?: Rect
): { baseX: number; baseY: number } | null {
  const limits = topLeftSlotViewportLimits(
    n,
    placementViewport,
    searchViewport,
    groupOverlapInsets,
    searchClip,
    options?.pouchTitles ? { pouchTitles: options.pouchTitles } : undefined,
  );
  if (!limits) return null;
  const { minBaseX, minBaseY, maxBaseX, maxBaseY } = limits;
  const layoutOptions = options?.pouchTitles
    ? { pouchTitles: options.pouchTitles }
    : undefined;
  const found = findNearestOnGrid(
    sourceX,
    sourceY,
    minBaseX,
    maxBaseX,
    minBaseY,
    maxBaseY,
    (x, y) => {
      const positions = computeBrickLayout(n, x, y, 'topleft', layoutOptions);
      return (
        checkSplitClusterPositions(
          positions,
          pouches,
          groups,
          placementViewport,
          options,
          searchInsets,
          groupOverlapInsets
        ) === null
      );
    }
  );
  return found ? { baseX: found.x, baseY: found.y } : null;
}

function clusterCenterViewportLimits(
  n: number,
  placementViewport: Viewport,
  searchViewport: Viewport,
  groupInsets: SplitGroupInsets
): { minCenterX: number; minCenterY: number; maxCenterX: number; maxCenterY: number } {
  const groupAtOrigin = computeSplitGroupBounds(
    computeBrickLayout(n, 0, 0, 'center'),
    groupInsets
  );
  const gap = PLACEMENT_GAP;
  return {
    minCenterX: placementViewport.left - groupAtOrigin.left,
    minCenterY: placementViewport.top - groupAtOrigin.top,
    maxCenterX: searchViewport.right - gap - groupAtOrigin.right,
    maxCenterY: searchViewport.bottom - gap - groupAtOrigin.bottom,
  };
}

type SplitClusterRejectReason =
  | 'pouch-outside-viewport'
  | 'pouch-overlap'
  | 'group-overlap';

function isForeignSplitPouch(
  p: Pouch,
  groups: Group[],
  excludeGroupIds: string[],
  excludePouchId: string
): boolean {
  if (p.id === excludePouchId) return false;
  if ((p as { _isDeleting?: boolean })._isDeleting) return false;
  const owner = findGroupForPouchId(p.id, groups);
  if (owner && excludeGroupIds.includes(owner.id)) return false;
  return true;
}

function foreignPouchesNearRect(
  rect: Rect,
  pouches: Pouch[],
  groups: Group[],
  excludeGroupIds: string[],
  excludePouchId: string,
  gap = PLACEMENT_GAP
): boolean {
  for (const p of pouches) {
    if (!isForeignSplitPouch(p, groups, excludeGroupIds, excludePouchId)) continue;
    if (rectsCloserThanGap(rect, pouchCircleBounds(p), gap)) {
      return true;
    }
  }
  return false;
}

/** Case A: 必要な辺だけ拡張（縦のみで足りるときは横を広げない） */
function computeMinimalExpandedParentBounds(
  parentBounds: Rect,
  positions: { x: number; y: number }[],
  insets: SplitGroupInsets
): Rect {
  if (splitClusterFitsInParentBounds(positions, parentBounds, insets)) {
    return parentBounds;
  }

  const memberBounds = computeClusterBounds(positions, 0, 0);
  const { left, right, top, bottom } = parentBounds;

  const membersFitHorizontally =
    memberBounds.left >= left + insets.left &&
    memberBounds.right <= right - insets.right;

  if (membersFitHorizontally) {
    return {
      left,
      top: Math.min(top, memberBounds.top - insets.top),
      right,
      bottom: Math.max(bottom, memberBounds.bottom + insets.bottom),
    };
  }

  const membersFitVertically =
    memberBounds.top >= top + insets.top &&
    memberBounds.bottom <= bottom - insets.bottom;

  if (membersFitVertically) {
    return {
      left: Math.min(left, memberBounds.left - insets.left),
      top,
      right: Math.max(right, memberBounds.right + insets.right),
      bottom,
    };
  }

  return {
    left: Math.min(left, memberBounds.left - insets.left),
    top: Math.min(top, memberBounds.top - insets.top),
    right: Math.max(right, memberBounds.right + insets.right),
    bottom: Math.max(bottom, memberBounds.bottom + insets.bottom),
  };
}

function checkSplitClusterPositions(
  positions: { x: number; y: number }[],
  pouches: Pouch[],
  groups: Group[],
  placementViewport: Viewport,
  options: ClusterPlacementOptions | undefined,
  searchInsets: SplitGroupInsets,
  groupOverlapInsets: SplitGroupInsets
): SplitClusterRejectReason | null {
  if (!isWithinViewport(positions, placementViewport)) {
    return 'pouch-outside-viewport';
  }
  if (!clusterMeetsPlacementOrigin(positions, groupOverlapInsets)) {
    return 'pouch-outside-viewport';
  }
  const envelopeClip = options?.placementEnvelopeClip;
  if (
    envelopeClip &&
    !splitClusterFitsInParentBounds(positions, envelopeClip, groupOverlapInsets)
  ) {
    return 'pouch-outside-viewport';
  }
  const mustNotFit = options?.mustNotFitInEnvelope;
  if (
    mustNotFit &&
    splitClusterFitsInParentBounds(positions, mustNotFit, groupOverlapInsets)
  ) {
    return 'group-overlap';
  }
  const excludePouchId = options?.excludePouchId;
  const excludeGroupIds = options?.excludeGroupIds ?? [];
  const mustAvoidGroupIds = options?.mustAvoidGroupIds ?? [];
  for (const pos of positions) {
    for (const existing of pouches) {
      if (existing.id === excludePouchId) continue;
      if ((existing as { _isDeleting?: boolean })._isDeleting) continue;
      if (circlesOverlap(pos, existing)) {
        return 'pouch-overlap';
      }
    }
  }
  const splitGroupBounds = computeSplitGroupBounds(positions, groupOverlapInsets);
  if (
    clusterOverlapsOtherGroups(
      splitGroupBounds,
      groups,
      excludeGroupIds,
      PLACEMENT_GAP
    )
  ) {
    return 'group-overlap';
  }
  if (
    mustAvoidGroupIds.length > 0 &&
    clusterOverlapsAnyGroup(
      splitGroupBounds,
      groups,
      mustAvoidGroupIds,
      PLACEMENT_GAP
    )
  ) {
    return 'group-overlap';
  }
  if (
    foreignPouchesNearRect(
      splitGroupBounds,
      pouches,
      groups,
      excludeGroupIds,
      excludePouchId ?? '',
      PLACEMENT_GAP
    )
  ) {
    return 'pouch-overlap';
  }
  return null;
}

function canPlaceSplitClusterAt(
  n: number,
  centerX: number,
  centerY: number,
  pouches: Pouch[],
  groups: Group[],
  placementViewport: Viewport,
  options: ClusterPlacementOptions | undefined,
  searchInsets: SplitGroupInsets,
  groupOverlapInsets: SplitGroupInsets
): SplitClusterRejectReason | null {
  const positions = computeBrickLayout(n, centerX, centerY, 'center');
  return checkSplitClusterPositions(
    positions,
    pouches,
    groups,
    placementViewport,
    options,
    searchInsets,
    groupOverlapInsets
  );
}

/** 南東象限候補: 元中心からの Manhattan 昇順、同距離は横優先（縦移動量が小さい方） */
function compareSoutheastByCenter(
  sourceX: number,
  sourceY: number,
  a: { centerX: number; centerY: number },
  b: { centerX: number; centerY: number }
): number {
  const dXA = a.centerX - sourceX;
  const dYA = a.centerY - sourceY;
  const dXB = b.centerX - sourceX;
  const dYB = b.centerY - sourceY;
  const manhattanA = dXA + dYA;
  const manhattanB = dXB + dYB;
  if (manhattanA !== manhattanB) return manhattanA - manhattanB;
  if (dYA !== dYB) return dYA - dYB;
  return dXA - dXB;
}

/** §2.4 試行2: 元位置から全方位・最短 Manhattan で center 塊を探索 */
export function findClusterNearestCenterSlot(
  n: number,
  sourceX: number,
  sourceY: number,
  pouches: Pouch[],
  groups: Group[],
  placementViewport: Viewport,
  options?: ClusterPlacementOptions,
  skipSourceCenter = false,
  searchInsets: SplitGroupInsets = computeSplitGroupInsets(false),
  searchViewport: Viewport = placementViewport,
  groupOverlapInsets: SplitGroupInsets = searchInsets,
  searchClip?: Rect
): { centerX: number; centerY: number } | null {
  const clippedSearch = searchClip
    ? clipViewportToRect(searchViewport, searchClip)
    : searchViewport;
  if (searchClip && !clippedSearch) {
    return null;
  }
  const effectiveSearch = clippedSearch ?? searchViewport;
  const { minCenterX, minCenterY, maxCenterX, maxCenterY } =
    clusterCenterViewportLimits(n, placementViewport, effectiveSearch, groupOverlapInsets);
  const sx = snapToGrid(sourceX);
  const sy = snapToGrid(sourceY);
  const found = findNearestOnGrid(
    sourceX,
    sourceY,
    minCenterX,
    maxCenterX,
    minCenterY,
    maxCenterY,
    (x, y) => {
      if (skipSourceCenter && x === sx && y === sy) return false;
      return (
        canPlaceSplitClusterAt(
          n,
          x,
          y,
          pouches,
          groups,
          placementViewport,
          options,
          searchInsets,
          groupOverlapInsets
        ) === null
      );
    }
  );
  return found ? { centerX: found.x, centerY: found.y } : null;
}

/** §2.4 試行2（レガシー）: 元位置の南東象限のみ。新規は findClusterNearestCenterSlot を使用 */
export function findClusterSoutheastCenterSlot(
  n: number,
  sourceX: number,
  sourceY: number,
  pouches: Pouch[],
  groups: Group[],
  placementViewport: Viewport,
  options?: ClusterPlacementOptions,
  skipSourceCenter = false,
  searchInsets: SplitGroupInsets = computeSplitGroupInsets(false),
  searchViewport: Viewport = placementViewport,
  groupOverlapInsets: SplitGroupInsets = searchInsets,
  searchClip?: Rect
): { centerX: number; centerY: number } | null {
  const startX = snapToGrid(sourceX);
  const startY = snapToGrid(sourceY);
  const refMemberBounds = clusterMemberBoundsAtCenter(n, startX, startY);
  const clippedSearch = searchClip
    ? clipViewportToRect(searchViewport, searchClip)
    : searchViewport;
  if (searchClip && !clippedSearch) {
    return null;
  }
  const effectiveSearch = clippedSearch ?? searchViewport;
  const { minCenterX, minCenterY, maxCenterX, maxCenterY } =
    clusterCenterViewportLimits(n, placementViewport, effectiveSearch, groupOverlapInsets);

  if (startX > maxCenterX || startY > maxCenterY) {
    return null;
  }

  const loopStartX = Math.max(startX, minCenterX);
  const loopStartY = Math.max(startY, minCenterY);

  const candidates: {
    centerX: number;
    centerY: number;
    memberLeft: number;
    memberTop: number;
  }[] = [];
  for (let y = loopStartY; y <= maxCenterY; y += GRID_SIZE) {
    for (let x = loopStartX; x <= maxCenterX; x += GRID_SIZE) {
      if (skipSourceCenter && x === startX && y === startY) {
        continue;
      }
      const memberBounds = clusterMemberBoundsAtCenter(n, x, y);
      if (
        memberBounds.left < refMemberBounds.left ||
        memberBounds.top < refMemberBounds.top
      ) {
        continue;
      }
      candidates.push({
        centerX: x,
        centerY: y,
        memberLeft: memberBounds.left,
        memberTop: memberBounds.top,
      });
    }
  }
  candidates.sort((a, b) =>
    compareSoutheastByCenter(sourceX, sourceY, a, b)
  );

  for (const { centerX, centerY } of candidates) {
    if (
      canPlaceSplitClusterAt(
        n,
        centerX,
        centerY,
        pouches,
        groups,
        placementViewport,
        options,
        searchInsets,
        groupOverlapInsets
      ) === null
    ) {
      return { centerX, centerY };
    }
  }
  return null;
}

/** 拡張リング（旧 bounds から new bounds へ広がった領域） */
function expansionRing(oldBounds: Rect, newBounds: Rect): Rect | null {
  if (
    oldBounds.left === newBounds.left &&
    oldBounds.top === newBounds.top &&
    oldBounds.right === newBounds.right &&
    oldBounds.bottom === newBounds.bottom
  ) {
    return null;
  }
  return {
    left: newBounds.left,
    top: newBounds.top,
    right: newBounds.right,
    bottom: newBounds.bottom,
  };
}

/** §2.3.2 周囲押し出し（cascade） */
export function displaceOverlapping(
  deltaX: number,
  deltaY: number,
  excludeGroupId: string | undefined,
  affectedRegion: Rect,
  pouches: Pouch[],
  groups: Group[],
  maxIterations = 50
): { pouches: Pouch[]; groups: Group[] } {
  if (deltaX === 0 && deltaY === 0) {
    return { pouches, groups };
  }

  let currentPouches = pouches.map(p => ({ ...p }));
  let currentGroups = groups.map(g => ({ ...g }));
  let region = { ...affectedRegion };

  for (let iter = 0; iter < maxIterations; iter++) {
    let moved = false;
    const nextPouches = currentPouches.map(p => ({ ...p }));
    const nextGroups = currentGroups.map(g => ({ ...g }));

    for (let i = 0; i < nextPouches.length; i++) {
      const p = nextPouches[i];
      if (p.isGroupTotalPouch) continue;
      const owner = findGroupForPouchId(p.id, currentGroups);
      if (owner?.id === excludeGroupId) continue;

      if (rectsIntersect(pouchCircleBounds(p), region)) {
        nextPouches[i] = { ...p, x: p.x + deltaX, y: p.y + deltaY };
        moved = true;
      }
    }

    for (let gi = 0; gi < nextGroups.length; gi++) {
      const g = nextGroups[gi];
      if (g.id === excludeGroupId) continue;
      const gb = getPathBounds(g.path) ?? getGroupPathBounds(g);
      if (!gb || !rectsIntersect(gb, region)) continue;

      nextGroups[gi] = { ...g, path: translatePath(g.path, deltaX, deltaY) };
      for (let pi = 0; pi < nextPouches.length; pi++) {
        const p = nextPouches[pi];
        const inGroup = g.pouches?.includes(p.id);
        const isGtp = p.isGroupTotalPouch && p.referencedGroupId === g.id;
        if (inGroup || isGtp) {
          nextPouches[pi] = { ...p, x: p.x + deltaX, y: p.y + deltaY };
        }
      }
      moved = true;
    }

    if (!moved) break;
    currentPouches = nextPouches;
    currentGroups = nextGroups;
    region = translateRect(region, deltaX, deltaY);
  }

  return { pouches: currentPouches, groups: currentGroups };
}

function isSplitRectPlacementClear(
  rect: Rect,
  viewport: Viewport,
  pouches: Pouch[],
  groups: Group[],
  excludeGroupIds: string[],
  excludePouchId: string
): boolean {
  if (!isRectWithinViewportWithGap(rect, viewport, PLACEMENT_GAP)) {
    return false;
  }
  if (clusterOverlapsOtherGroups(rect, groups, excludeGroupIds, PLACEMENT_GAP)) {
    return false;
  }
  return !foreignPouchesNearRect(
    rect,
    pouches,
    groups,
    excludeGroupIds,
    excludePouchId,
    PLACEMENT_GAP
  );
}

function mergeViewports(a: Viewport, b: Viewport): Viewport {
  return {
    left: Math.min(a.left, b.left),
    top: Math.min(a.top, b.top),
    right: Math.max(a.right, b.right),
    bottom: Math.max(b.bottom, b.bottom),
  };
}

/** Case A 移動先探索: 南東方向に modest 余白 */
export function expandViewportForSplitRelocate(viewport: Viewport, margin = 480): Viewport {
  return {
    ...viewport,
    right: viewport.right + margin,
    bottom: viewport.bottom + margin,
  };
}

/** calculator-area の getBoundingClientRect から、表示中の内容座標 viewport を算出 */
export function getVisibleContentViewport(
  areaRect: Rect,
  windowWidth: number,
  windowHeight: number
): Viewport {
  const visibleLeft = Math.max(0, areaRect.left);
  const visibleTop = Math.max(0, areaRect.top);
  const visibleRight = Math.min(windowWidth, areaRect.right);
  const visibleBottom = Math.min(windowHeight, areaRect.bottom);
  return {
    left: visibleLeft - areaRect.left,
    top: visibleTop - areaRect.top,
    right: visibleRight - areaRect.left,
    bottom: visibleBottom - areaRect.top,
  };
}

/** 内容座標の矩形が表示 viewport 内に収まっているか（padding 付き） */
export function isRectVisibleInContentViewport(
  rect: Rect,
  visibleViewport: Viewport,
  padding = PLACEMENT_GAP
): boolean {
  return isRectWithinViewportWithGap(rect, visibleViewport, padding);
}

export interface RevealScrollOptions {
  padding?: number;
  /** 左端の最小表示 X（サイドバー幅など） */
  minVisibleLeft?: number;
}

/**
 * 内容座標 bounds が画面外なら、window.scrollBy 用の最小 delta（px）を返す。
 * areaRect は calculator-area の getBoundingClientRect 相当。
 */
export function computeRevealScrollDelta(
  bounds: Rect,
  areaRect: Rect,
  windowWidth: number,
  windowHeight: number,
  options: RevealScrollOptions = {}
): { dx: number; dy: number } {
  const padding = options.padding ?? 48;
  const minVisibleLeft = options.minVisibleLeft ?? 0;

  const screenLeft = areaRect.left + bounds.left;
  const screenTop = areaRect.top + bounds.top;
  const screenRight = areaRect.left + bounds.right;
  const screenBottom = areaRect.top + bounds.bottom;

  let dx = 0;
  let dy = 0;

  // 横長 bounds は左端優先（右端だけ見えていて左が切れているケース）
  if (screenLeft < minVisibleLeft + padding) {
    dx = screenLeft - (minVisibleLeft + padding);
  } else if (screenRight > windowWidth - padding) {
    dx = screenRight - (windowWidth - padding);
  }

  if (screenBottom > windowHeight - padding) {
    dy = screenBottom - (windowHeight - padding);
  } else if (screenTop < padding) {
    dy = screenTop - padding;
  }

  return { dx, dy };
}

function mergeRects(a: Rect | null, b: Rect): Rect {
  if (!a) return b;
  return {
    left: Math.min(a.left, b.left),
    top: Math.min(a.top, b.top),
    right: Math.max(a.right, b.right),
    bottom: Math.max(a.bottom, b.bottom),
  };
}

function pouchContentRect(p: Pouch): Rect {
  return {
    left: p.x,
    top: p.y,
    right: p.x + POUCH_DIAMETER,
    bottom: p.y + POUCH_DIAMETER,
  };
}

function groupMembershipKey(pouchIds: string[] | undefined): string {
  return [...(pouchIds ?? [])].sort().join('\0');
}

function groupRevealBounds(group: Group, pouches: Pouch[]): Rect | null {
  let bounds: Rect | null = getGroupPathBounds(group);
  const memberBounds = boundsFromGroupMemberPouches(group, pouches);
  if (memberBounds) {
    bounds = mergeRects(bounds, memberBounds);
  }
  return bounds;
}

/**
 * undo/redo 後に画面外へ行った要素の bounds（次状態の位置）。
 * 位置・path・メンバーが変わったパウチ／グループのみ。値のみの変更は null。
 */
export function computeUndoRedoRevealBounds(
  prevPouches: Pouch[],
  prevGroups: Group[],
  nextPouches: Pouch[],
  nextGroups: Group[],
): Rect | null {
  let bounds: Rect | null = null;
  const prevPouchMap = new Map(prevPouches.map(p => [p.id, p]));
  const prevGroupMap = new Map(prevGroups.map(g => [g.id, g]));
  const nextPouchIds = new Set(nextPouches.map(p => p.id));

  for (const p of nextPouches) {
    if ((p as { _isDeleting?: boolean })._isDeleting) continue;
    const prev = prevPouchMap.get(p.id);
    if (!prev || prev.x !== p.x || prev.y !== p.y) {
      bounds = mergeRects(bounds, pouchContentRect(p));
      if (prev) {
        bounds = mergeRects(bounds, pouchContentRect(prev));
      }
    }
  }

  for (const p of prevPouches) {
    if (!nextPouchIds.has(p.id)) {
      bounds = mergeRects(bounds, pouchContentRect(p));
    }
  }

  for (const g of nextGroups) {
    if ((g as { _isDeleting?: boolean })._isDeleting) continue;
    const prev = prevGroupMap.get(g.id);
    const membershipChanged =
      !prev || groupMembershipKey(prev.pouches) !== groupMembershipKey(g.pouches);
    const pathChanged = !prev || prev.path !== g.path;
    if (!pathChanged && !membershipChanged) continue;

    const b = groupRevealBounds(g, nextPouches);
    if (b) {
      bounds = mergeRects(bounds, b);
    }
    if (prev) {
      const prevB = groupRevealBounds(prev, prevPouches);
      if (prevB) {
        bounds = mergeRects(bounds, prevB);
      }
    }
  }

  for (const g of prevGroups) {
    if (nextGroups.some(ng => ng.id === g.id)) continue;
    const b = groupRevealBounds(g, prevPouches);
    if (b) {
      bounds = mergeRects(bounds, b);
    }
  }

  return bounds;
}

/** 矩形 bounds が収まるよう viewport を拡張（left/top は 0 まで緩める） */
function ensureViewportFitsRect(viewport: Viewport, bounds: Rect, gap = PLACEMENT_GAP): Viewport {
  const width = bounds.right - bounds.left;
  const height = bounds.bottom - bounds.top;
  return {
    left: Math.min(viewport.left, 0),
    top: Math.min(viewport.top, 0),
    right: Math.max(viewport.right, viewport.left + gap * 2 + width),
    bottom: Math.max(viewport.bottom, viewport.top + gap * 2 + height),
  };
}

const SPLIT_RELOCATE_MAX_MARGIN = 10000;

function maxRelocateMargin(_n: number, _bounds: Rect): number {
  return SPLIT_RELOCATE_MAX_MARGIN;
}

/** 元位置 anchor の近傍だけを移動先探索範囲にする */
function computeLocalRelocateSearchViewport(
  anchorBounds: Rect,
  bounds: Rect,
  baseViewport: Viewport,
  localMargin: number
): Viewport {
  const local: Viewport = {
    left: Math.max(0, anchorBounds.left - localMargin),
    top: Math.max(0, anchorBounds.top - localMargin),
    right: anchorBounds.right + localMargin,
    bottom: anchorBounds.bottom + localMargin,
  };
  return ensureViewportFitsRect(mergeViewports(baseViewport, local), bounds);
}

function findClearRectSlot(
  bounds: Rect,
  viewport: Viewport,
  pouches: Pouch[],
  groups: Group[],
  excludeGroupIds: string[],
  excludePouchId: string,
  anchorBounds: Rect,
  maxMargin: number
): { left: number; top: number } | null {
  const width = bounds.right - bounds.left;
  const height = bounds.bottom - bounds.top;
  const gap = PLACEMENT_GAP;
  const effectiveViewport = ensureViewportFitsRect(viewport, bounds, gap);
  const minLeft = effectiveViewport.left + gap;
  const minTop = effectiveViewport.top + gap;
  const maxLeft = effectiveViewport.right - gap - width;
  const maxTop = effectiveViewport.bottom - gap - height;
  if (maxLeft < minLeft || maxTop < minTop) {
    return null;
  }

  /** 空き地の一番左上（first-fit 上→下・左→右） */
  for (let y = minTop; y <= maxTop; y += GRID_SIZE) {
    for (let x = minLeft; x <= maxLeft; x += GRID_SIZE) {
      if (
        Math.abs(x - anchorBounds.left) > maxMargin ||
        Math.abs(y - anchorBounds.top) > maxMargin
      ) {
        continue;
      }
      const rect = { left: x, top: y, right: x + width, bottom: y + height };
      if (
        isSplitRectPlacementClear(
          rect,
          effectiveViewport,
          pouches,
          groups,
          excludeGroupIds,
          excludePouchId
        )
      ) {
        return { left: x, top: y };
      }
    }
  }
  return null;
}

function translateGroupSubtree(
  groupId: string,
  deltaX: number,
  deltaY: number,
  pouches: Pouch[],
  groups: Group[]
): { pouches: Pouch[]; groups: Group[] } {
  if (deltaX === 0 && deltaY === 0) {
    return { pouches, groups };
  }
  const group = groups.find(g => g.id === groupId);
  if (!group) {
    return { pouches, groups };
  }
  const memberIds = new Set(group.pouches ?? []);
  const updatedGroups = groups.map(g =>
    g.id === groupId ? { ...g, path: translatePath(g.path, deltaX, deltaY) } : g
  );
  const updatedPouches = pouches.map(p => {
    if (memberIds.has(p.id)) {
      return { ...p, x: p.x + deltaX, y: p.y + deltaY };
    }
    if (p.isGroupTotalPouch && p.referencedGroupId === groupId) {
      return { ...p, x: p.x + deltaX, y: p.y + deltaY };
    }
    return p;
  });
  return { pouches: updatedPouches, groups: updatedGroups };
}

function setGroupPathFromBounds(groupId: string, bounds: Rect, groups: Group[]): Group[] {
  const path = generateRoundedRectPath(
    bounds.left,
    bounds.top,
    bounds.right,
    bounds.bottom
  );
  return groups.map(g => (g.id === groupId ? { ...g, path } : g));
}

/** split Case A: path 拡張のみ（押し出しなし）＋祖先へ再帰 */
function expandGroupBoundsOnlyWithAncestors(
  groupId: string,
  requiredBounds: Rect,
  groups: Group[]
): Group[] {
  let updated = setGroupPathFromBounds(groupId, requiredBounds, groups);
  let currentId = groupId;
  let depth = groups.length;

  while (depth-- > 0) {
    const childGroup = updated.find(g => g.id === currentId);
    const childBounds = childGroup ? getGroupPathBounds(childGroup) : null;
    if (!childBounds) break;

    const container = findInnermostContainerGroup(currentId, updated);
    if (!container) break;

    const containerBounds = getGroupPathBounds(container);
    if (!containerBounds || rectFullyContains(containerBounds, childBounds)) {
      break;
    }

    const newContainerBounds = unionRects(containerBounds, childBounds);
    updated = setGroupPathFromBounds(container.id, newContainerBounds, updated);
    currentId = container.id;
  }

  return updated;
}

/**
 * split Case A: 親を cluster 分だけ拡張。viewport 外・障害物と干渉する場合は
 * 親＋メンバーを空きスロットへ移動してから拡張（押し出しは使わない）。
 */
export function prepareSoleMemberParentForSplit(
  parentGroupId: string,
  sourcePouch: Pouch,
  n: number,
  pouches: Pouch[],
  groups: Group[],
  placementViewport: Viewport,
  searchViewport: Viewport,
  displayInsets: SplitGroupInsets,
  overlapExcludeGroupIds: string[]
): { pouches: Pouch[]; groups: Group[]; sourcePouch: Pouch } | null {
  const parentGroup = groups.find(g => g.id === parentGroupId);
  if (!parentGroup) {
    return null;
  }

  const searchInsets = displayInsets;

  let workingPouches = pouches;
  let workingGroups = groups;
  let workingSource = sourcePouch;

  const excludeIds = overlapExcludeGroupIds.length
    ? overlapExcludeGroupIds
    : [parentGroupId];

  const recomputeNeededBounds = (): Rect | null => {
    const parentBounds = getGroupPathBounds(
      workingGroups.find(g => g.id === parentGroupId)!
    );
    if (!parentBounds) return null;
    const expandPositions = computeBrickLayout(
      n,
      workingSource.x,
      workingSource.y,
      'center'
    );
    return computeMinimalExpandedParentBounds(
      parentBounds,
      expandPositions,
      searchInsets
    );
  };

  let neededBounds = recomputeNeededBounds();
  if (!neededBounds) {
    return null;
  }

  const anchorBounds = { ...neededBounds };
  const baseViewport = mergeViewports(placementViewport, searchViewport);
  const maxMargin = maxRelocateMargin(n, anchorBounds);
  const relocateViewport = computeLocalRelocateSearchViewport(
    anchorBounds,
    neededBounds,
    baseViewport,
    maxMargin
  );

  const isPlacementReady = (bounds: Rect): boolean =>
    isSplitRectPlacementClear(
      bounds,
      ensureViewportFitsRect(relocateViewport, bounds),
      workingPouches,
      workingGroups,
      excludeIds,
      sourcePouch.id
    );

  for (let relocateAttempt = 0; relocateAttempt < 8; relocateAttempt++) {
    if (isPlacementReady(neededBounds)) {
      break;
    }

    const slot = findClearRectSlot(
      neededBounds,
      relocateViewport,
      workingPouches,
      workingGroups,
      excludeIds,
      sourcePouch.id,
      anchorBounds,
      maxMargin
    );

    if (!slot) {
      return null;
    }

    const deltaX = slot.left - neededBounds.left;
    const deltaY = slot.top - neededBounds.top;
    if (deltaX === 0 && deltaY === 0) {
      return null;
    }

    const moved = translateGroupSubtree(
      parentGroupId,
      deltaX,
      deltaY,
      workingPouches,
      workingGroups
    );
    workingPouches = moved.pouches;
    workingGroups = moved.groups;
    workingSource = workingPouches.find(p => p.id === sourcePouch.id) ?? {
      ...sourcePouch,
      x: sourcePouch.x + deltaX,
      y: sourcePouch.y + deltaY,
    };
    neededBounds = recomputeNeededBounds();
    if (!neededBounds) {
      return null;
    }
  }

  if (!isPlacementReady(neededBounds)) {
    return null;
  }

  workingGroups = expandGroupBoundsOnlyWithAncestors(
    parentGroupId,
    neededBounds,
    workingGroups
  );

  return {
    pouches: workingPouches,
    groups: workingGroups,
    sourcePouch: workingSource,
  };
}

/** §2.3.1 グループ拡張 + 押し出し（モード B / 将来 API 用） */
export function expandGroupForCluster(
  groupId: string,
  clusterBounds: Rect,
  pouches: Pouch[],
  groups: Group[]
): { pouches: Pouch[]; groups: Group[] } {
  const group = groups.find(g => g.id === groupId);
  if (!group) return { pouches, groups };

  const oldBounds = getGroupPathBounds(group);
  if (!oldBounds) return { pouches, groups };

  const newBounds = unionRects(oldBounds, clusterBounds);
  if (
    oldBounds.left === newBounds.left &&
    oldBounds.top === newBounds.top &&
    oldBounds.right === newBounds.right &&
    oldBounds.bottom === newBounds.bottom
  ) {
    return { pouches, groups };
  }

  const deltaRight = Math.max(0, newBounds.right - oldBounds.right);
  const deltaBottom = Math.max(0, newBounds.bottom - oldBounds.bottom);
  const deltaLeft = Math.max(0, oldBounds.left - newBounds.left);
  const deltaTop = Math.max(0, oldBounds.top - newBounds.top);

  let deltaX = 0;
  let deltaY = 0;
  const expandChoices: { deltaX: number; deltaY: number; amount: number }[] = [];
  if (deltaBottom > 0) {
    expandChoices.push({ deltaX: 0, deltaY: deltaBottom, amount: deltaBottom });
  }
  if (deltaRight > 0) {
    expandChoices.push({ deltaX: deltaRight, deltaY: 0, amount: deltaRight });
  }
  if (deltaTop > 0) {
    expandChoices.push({ deltaX: 0, deltaY: -deltaTop, amount: deltaTop });
  }
  if (deltaLeft > 0) {
    expandChoices.push({ deltaX: -deltaLeft, deltaY: 0, amount: deltaLeft });
  }
  expandChoices.sort((a, b) => {
    if (a.amount !== b.amount) return a.amount - b.amount;
    const aHorizontal = a.deltaX !== 0;
    const bHorizontal = b.deltaX !== 0;
    if (aHorizontal !== bHorizontal) return aHorizontal ? -1 : 1;
    return 0;
  });
  if (expandChoices.length > 0) {
    deltaX = expandChoices[0].deltaX;
    deltaY = expandChoices[0].deltaY;
  }

  const newPath = generateRoundedRectPath(
    newBounds.left,
    newBounds.top,
    newBounds.right,
    newBounds.bottom
  );
  const updatedGroups = groups.map(g =>
    g.id === groupId ? { ...g, path: newPath } : g
  );

  const ring = expansionRing(oldBounds, newBounds);
  if (!ring || (deltaX === 0 && deltaY === 0)) {
    return { pouches, groups: updatedGroups };
  }

  return displaceOverlapping(deltaX, deltaY, groupId, ring, pouches, updatedGroups);
}

function rectFullyContains(outer: Rect, inner: Rect): boolean {
  return (
    inner.left >= outer.left &&
    inner.right <= outer.right &&
    inner.top >= outer.top &&
    inner.bottom <= outer.bottom
  );
}

/**
 * §2.3.1 拡張 + 押し出しを、内包している祖父グループ方向へ再帰的に繰り返す。
 * 子 G の path 拡張後、最内側の親 B に収まらなければ B も拡張し、さらに A … と辿る。
 */
export function expandGroupForClusterWithAncestors(
  groupId: string,
  clusterBounds: Rect,
  pouches: Pouch[],
  groups: Group[]
): { pouches: Pouch[]; groups: Group[] } {
  let state = expandGroupForCluster(groupId, clusterBounds, pouches, groups);
  let currentId = groupId;
  let depth = groups.length;

  while (depth-- > 0) {
    const childGroup = state.groups.find(g => g.id === currentId);
    const childBounds = childGroup ? getGroupPathBounds(childGroup) : null;
    if (!childBounds) break;

    const container = findInnermostContainerGroup(currentId, state.groups);
    if (!container) break;

    const containerBounds = getGroupPathBounds(container);
    if (!containerBounds || rectFullyContains(containerBounds, childBounds)) {
      break;
    }

    state = expandGroupForCluster(
      container.id,
      childBounds,
      state.pouches,
      state.groups
    );
    currentId = container.id;
  }

  return state;
}

/**
 * §2.4 分解配置: 中心優先 → 全方位・最短距離（center / top-left）
 */
function splitPlacementDebug(step: string, detail?: Record<string, unknown>): void {
  if (process.env.NODE_ENV === 'development') {
    console.debug('[POUCHES split]', step, detail ?? {});
  }
}

type SplitPlacementAttemptResult =
  | {
      status: 'success';
      positions: { x: number; y: number }[];
      attempt: 'center' | 'nearest-center' | 'nearest-top-left' | 'top-left';
      inGroup: SplitInGroupContext;
      splitGroupBounds: Rect;
      placedOutsideEnvelope?: boolean;
    }
  | {
      status: 'failed';
      inGroup: SplitInGroupContext;
      reason: string;
    };

function attemptSplitPlacementSearch(
  sourcePouch: Pouch,
  n: number,
  pouches: Pouch[],
  groups: Group[],
  placementViewport: Viewport,
  searchViewport: Viewport,
  searchInsets: ReturnType<typeof computeSplitGroupInsets>,
  groupOverlapInsets: ReturnType<typeof computeSplitGroupInsets>,
  displayInsets: ReturnType<typeof computeSplitGroupInsets>,
  frozenOverlapExcludeGroupIds?: string[]
): SplitPlacementAttemptResult {
  const resolvedInGroup = resolveSplitInGroupContext(sourcePouch, groups, pouches);
  const inGroup =
    frozenOverlapExcludeGroupIds && frozenOverlapExcludeGroupIds.length > 0
      ? { ...resolvedInGroup, overlapExcludeGroupIds: frozenOverlapExcludeGroupIds }
      : resolvedInGroup;
  const placementOptions: ClusterPlacementOptions = {
    excludePouchId: sourcePouch.id,
    excludeGroupIds: inGroup.overlapExcludeGroupIds ?? [],
    mustAvoidGroupIds: inGroup.mustAvoidGroupIds ?? [],
  };
  const sourceGroup = inGroup.parentGroupId
    ? groups.find(g => g.id === inGroup.parentGroupId)
    : findGroupForPouchId(sourcePouch.id, groups);
  const hasTitle = !!(
    sourcePouch.title?.trim() ||
    sourceGroup?.title?.trim()
  );

  let basePlacementViewport = placementViewport;
  let baseSearchViewport = searchViewport;

  if (inGroup.mode === 'sole-member' && inGroup.parentBounds) {
    const parentBoundsHint =
      inGroup.parentGroupId && inGroup.parentBounds
        ? resolveSoleMemberPlacementEnvelope(
            inGroup.parentGroupId,
            inGroup.parentBounds,
            groups
          )
        : inGroup.parentBounds;
    const ensured = ensureSplitSearchViewportForCluster(
      basePlacementViewport,
      baseSearchViewport,
      n,
      groupOverlapInsets,
      {
        parentBounds: parentBoundsHint,
        sourcePouch,
        mustAvoidGroupIds: [],
        groups,
        pouches,
      }
    );
    basePlacementViewport = ensured.placementViewport;
    baseSearchViewport = ensured.searchViewport;
  } else if (inGroup.mode === 'multi-member' || inGroup.mode === 'ungrouped') {
    const parentBounds =
      inGroup.parentBounds ??
      (inGroup.parentGroupId
        ? getGroupPathBounds(groups.find(g => g.id === inGroup.parentGroupId)!) ?? null
        : null);
    const ensured = ensureSplitSearchViewportForCluster(
      basePlacementViewport,
      baseSearchViewport,
      n,
      groupOverlapInsets,
      {
        parentBounds,
        sourcePouch,
        mustAvoidGroupIds: placementOptions.mustAvoidGroupIds,
        groups,
        pouches,
      }
    );
    basePlacementViewport = ensured.placementViewport;
    baseSearchViewport = ensured.searchViewport;
  }

  const searchPhases: SoleMemberSplitSearchPhase[] =
    inGroup.mode === 'sole-member' &&
    inGroup.parentBounds &&
    inGroup.parentGroupId &&
    frozenOverlapExcludeGroupIds &&
    frozenOverlapExcludeGroupIds.length > 0
      ? buildSoleMemberSplitSearchPhases(
          inGroup.parentGroupId,
          inGroup.parentBounds,
          groups,
          frozenOverlapExcludeGroupIds
        )
      : [
          {
            excludeGroupIds: inGroup.overlapExcludeGroupIds ?? [],
            placedOutsideEnvelope: false,
          },
        ];

  let positions: { x: number; y: number }[] | undefined;
  let attempt: 'center' | 'nearest-center' | 'nearest-top-left' | 'top-left' = 'center';
  let placedOutsideEnvelope = false;

  for (let phaseIndex = 0; phaseIndex < searchPhases.length; phaseIndex++) {
    const phase = searchPhases[phaseIndex];
    positions = undefined;
    let effectivePlacementViewport = basePlacementViewport;
    let effectiveSearchViewport = baseSearchViewport;
    const searchClip = phase.searchClip;
    const phaseOptions: ClusterPlacementOptions = {
      excludePouchId: sourcePouch.id,
      excludeGroupIds: phase.excludeGroupIds,
      mustAvoidGroupIds: inGroup.mustAvoidGroupIds ?? [],
      placementEnvelopeClip: phase.envelopeClip,
      mustNotFitInEnvelope: phase.mustNotFitInEnvelope,
    };
    const isSoleMemberPhase =
      inGroup.mode === 'sole-member' && (!!phase.envelopeClip || phase.placedOutsideEnvelope);

    splitPlacementDebug('planSplitPlacement start', {
      pouchId: sourcePouch.id,
      n,
      mode: inGroup.mode,
      phase: phaseIndex,
      placedOutsideEnvelope: phase.placedOutsideEnvelope,
      createNewGroup: inGroup.createNewGroup,
      sourceGroupId: inGroup.parentGroupId ?? null,
      excludeGroupIds: phaseOptions.excludeGroupIds ?? null,
      mustAvoidGroupIds: phaseOptions.mustAvoidGroupIds ?? null,
      hasTitle,
      searchInsets,
      groupOverlapInsets,
      placementViewport: effectivePlacementViewport,
      searchViewport: effectiveSearchViewport,
      searchClip: searchClip ?? null,
    });

    if (phase.envelopeClip && searchClip) {
      const topLeftPositions = trySoleMemberTopLeftPlacement(
        n,
        sourcePouch.x,
        sourcePouch.y,
        pouches,
        groups,
        searchClip,
        effectivePlacementViewport,
        effectiveSearchViewport,
        phaseOptions,
        searchInsets,
        displayInsets
      );
      if (topLeftPositions) {
        attempt = 'top-left';
        positions = topLeftPositions;
      }
    }

    if (!positions) {
      const centerReject = canPlaceSplitClusterAt(
        n,
        sourcePouch.x,
        sourcePouch.y,
        pouches,
        groups,
        effectivePlacementViewport,
        phaseOptions,
        searchInsets,
        groupOverlapInsets
      );
      const centerLayout = computeBrickLayout(n, sourcePouch.x, sourcePouch.y, 'center');
      const centerFitsClip =
        !searchClip ||
        splitClusterFitsInParentBounds(centerLayout, searchClip, displayInsets);
      if (centerReject === null && centerFitsClip) {
        attempt = 'center';
        positions = centerLayout;
      } else {
        if (centerReject !== null) {
          splitPlacementDebug('planSplitPlacement center failed', {
            ...phaseOptions,
            reason: centerReject,
          });
        }
        const nearestCenterSlot = findClusterNearestCenterSlot(
          n,
          sourcePouch.x,
          sourcePouch.y,
          pouches,
          groups,
          effectivePlacementViewport,
          phaseOptions,
          true,
          searchInsets,
          effectiveSearchViewport,
          groupOverlapInsets,
          searchClip
        );
        if (nearestCenterSlot) {
          attempt = 'nearest-center';
          positions = computeBrickLayout(
            n,
            nearestCenterSlot.centerX,
            nearestCenterSlot.centerY,
            'center'
          );
        } else {
          splitPlacementDebug('planSplitPlacement nearest-center failed', phaseOptions);
          let slot = findClusterNearestTopLeftSlot(
            n,
            sourcePouch.x,
            sourcePouch.y,
            pouches,
            groups,
            effectivePlacementViewport,
            phaseOptions,
            searchInsets,
            effectiveSearchViewport,
            groupOverlapInsets,
            searchClip
          );
          if (
            !slot &&
            (isSoleMemberPhase ||
              inGroup.mode === 'multi-member' ||
              inGroup.mode === 'ungrouped')
          ) {
            const mega = ensureSplitSearchViewportForCluster(
              effectivePlacementViewport,
              effectiveSearchViewport,
              n,
              groupOverlapInsets,
              {
                parentBounds: phase.placedOutsideEnvelope
                  ? null
                  : searchClip ?? inGroup.parentBounds ?? null,
                sourcePouch,
                mustAvoidGroupIds: phaseOptions.mustAvoidGroupIds,
                groups,
                pouches,
                extraMargin: SPLIT_RELOCATE_MAX_MARGIN,
              }
            );
            slot = findClusterNearestTopLeftSlot(
              n,
              sourcePouch.x,
              sourcePouch.y,
              pouches,
              groups,
              mega.placementViewport,
              phaseOptions,
              searchInsets,
              mega.searchViewport,
              groupOverlapInsets,
              searchClip
            );
            if (slot) {
              effectivePlacementViewport = mega.placementViewport;
              effectiveSearchViewport = mega.searchViewport;
            }
          }
          if (slot) {
            attempt = 'nearest-top-left';
            positions = computeBrickLayout(n, slot.baseX, slot.baseY, 'topleft');
          } else {
            const probeOrigin = computeSplitGroupBounds(
              computeBrickLayout(n, 0, 0, 'topleft'),
              groupOverlapInsets
            );
            splitPlacementDebug('planSplitPlacement nearest-top-left failed', {
              ...phaseOptions,
              clusterW: probeOrigin.right - probeOrigin.left,
              clusterH: probeOrigin.bottom - probeOrigin.top,
              minBaseX:
                effectivePlacementViewport.left +
                PLACEMENT_GAP -
                probeOrigin.left,
              maxBaseX:
                effectiveSearchViewport.right -
                PLACEMENT_GAP -
                probeOrigin.right,
              placementViewport: effectivePlacementViewport,
              searchViewport: effectiveSearchViewport,
            });
          }
        }
      }
    }

    if (positions) {
      placedOutsideEnvelope = phase.placedOutsideEnvelope;
      break;
    }
  }

  if (!positions) {
    return {
      status: 'failed',
      inGroup,
      reason:
        inGroup.mode === 'sole-member'
          ? 'sole-member no slot in envelope or outside'
          : 'no slot in viewport',
    };
  }

  const splitGroupBounds = computeSplitGroupBounds(positions, displayInsets);

  const insideEnvelope = searchPhases[0]?.searchClip;
  if (
    inGroup.mode === 'sole-member' &&
    !placedOutsideEnvelope &&
    insideEnvelope &&
    !splitClusterFitsInParentBounds(positions, insideEnvelope, displayInsets)
  ) {
    return {
      status: 'failed',
      inGroup,
      reason: 'sole-member cluster outside placement envelope',
    };
  }

  splitPlacementDebug('planSplitPlacement ok', {
    attempt,
    mode: inGroup.mode,
    createNewGroup: inGroup.createNewGroup,
    placedOutsideEnvelope,
    positionsCount: positions.length,
    splitGroupBounds,
  });

  return {
    status: 'success',
    positions,
    attempt,
    inGroup,
    splitGroupBounds,
    placedOutsideEnvelope,
  };
}

export function planSplitPlacement(
  sourcePouch: Pouch,
  n: number,
  pouches: Pouch[],
  groups: Group[],
  placementViewport: Viewport,
  searchViewport: Viewport = placementViewport
): SplitPlacementPlan | null {
  const initialInGroup = resolveSplitInGroupContext(sourcePouch, groups, pouches);
  const frozenOverlapExcludeGroupIds =
    initialInGroup.mode === 'sole-member' && initialInGroup.parentGroupId
      ? collectSoleMemberOverlapExcludeGroupIds(
          initialInGroup.parentGroupId,
          sourcePouch,
          groups
        )
      : initialInGroup.overlapExcludeGroupIds;
  const initialSourceGroup = initialInGroup.parentGroupId
    ? groups.find(g => g.id === initialInGroup.parentGroupId)
    : findGroupForPouchId(sourcePouch.id, groups);
  const hasTitle = !!(
    sourcePouch.title?.trim() ||
    initialSourceGroup?.title?.trim()
  );
  const searchInsets = computeSplitGroupInsets(false);
  const groupOverlapInsets = computeSplitGroupInsets(hasTitle);
  const displayInsets = groupOverlapInsets;

  let workingPouches = pouches;
  let workingGroups = groups;
  let workingSource = sourcePouch;
  let effectivePlacement = placementViewport;
  let effectiveSearch = searchViewport;

  const initialExpand = ensureSplitSearchViewportForCluster(
    effectivePlacement,
    effectiveSearch,
    n,
    groupOverlapInsets,
    {
      parentBounds:
        initialInGroup.mode === 'sole-member' &&
        initialInGroup.parentGroupId &&
        initialInGroup.parentBounds
          ? resolveSoleMemberPlacementEnvelope(
              initialInGroup.parentGroupId,
              initialInGroup.parentBounds,
              groups
            )
          : initialInGroup.parentBounds ?? null,
      sourcePouch,
      mustAvoidGroupIds: initialInGroup.mustAvoidGroupIds,
      groups,
      pouches,
    }
  );
  effectivePlacement = initialExpand.placementViewport;
  effectiveSearch = initialExpand.searchViewport;

  for (let expandRetry = 0; expandRetry < 2; expandRetry++) {
    const result = attemptSplitPlacementSearch(
      workingSource,
      n,
      workingPouches,
      workingGroups,
      effectivePlacement,
      effectiveSearch,
      searchInsets,
      groupOverlapInsets,
      displayInsets,
      frozenOverlapExcludeGroupIds
    );

    if (result.status === 'success') {
      if (
        !result.inGroup.createNewGroup &&
        result.inGroup.parentGroupId
      ) {
        workingGroups = result.placedOutsideEnvelope
          ? setGroupPathFromBounds(
              result.inGroup.parentGroupId,
              result.splitGroupBounds,
              workingGroups
            )
          : expandGroupBoundsOnlyWithAncestors(
              result.inGroup.parentGroupId,
              result.splitGroupBounds,
              workingGroups
            );
      }
      return {
        positions: result.positions,
        pouches: workingPouches,
        groups: workingGroups,
        splitGroupBounds: result.splitGroupBounds,
        createNewGroup: result.inGroup.createNewGroup,
        parentGroupId: result.inGroup.parentGroupId,
      };
    }

    const { inGroup, reason } = result;
    if (
      expandRetry === 0 &&
      inGroup.mode === 'sole-member' &&
      inGroup.parentGroupId
    ) {
      const prepared = prepareSoleMemberParentForSplit(
        inGroup.parentGroupId,
        workingSource,
        n,
        workingPouches,
        workingGroups,
        effectivePlacement,
        effectiveSearch,
        displayInsets,
        frozenOverlapExcludeGroupIds ?? inGroup.overlapExcludeGroupIds ?? [inGroup.parentGroupId]
      );
      if (!prepared) {
        splitPlacementDebug('planSplitPlacement abort', {
          reason: 'sole-member expand/prepare failed',
          detail: reason,
        });
        return null;
      }
      workingPouches = prepared.pouches;
      workingGroups = prepared.groups;
      workingSource = prepared.sourcePouch;

      const contentViewport = computePlacementViewport(workingPouches, workingGroups);
      effectivePlacement = expandPlacementViewportForSplit(
        mergeViewports(mergeViewports(placementViewport, contentViewport), effectivePlacement),
        workingSource,
        n,
        searchInsets
      );
      effectiveSearch = expandViewportForSplitRelocate(
        mergeViewports(effectivePlacement, effectiveSearch)
      );

      splitPlacementDebug('planSplitPlacement expand retry', {
        reason,
        parentGroupId: inGroup.parentGroupId,
        effectivePlacement,
        effectiveSearch,
      });
      continue;
    }

    splitPlacementDebug('planSplitPlacement abort', { reason, parentBounds: inGroup.parentBounds });
    return null;
  }

  return null;
}

/** 配置用 viewport を既存コンテンツから算出（パウチ中心座標系） */
export function computePlacementViewport(
  pouches: Pouch[],
  groups: Group[],
  edgeBuffer = 0
): Viewport {
  let right = 400;
  let bottom = 300;
  for (const p of pouches) {
    if ((p as { _isDeleting?: boolean })._isDeleting) continue;
    right = Math.max(right, p.x + POUCH_RADIUS);
    bottom = Math.max(bottom, p.y + POUCH_RADIUS);
  }
  for (const g of groups) {
    const b = getGroupPathBounds(g);
    if (b) {
      right = Math.max(right, b.right);
      bottom = Math.max(bottom, b.bottom);
    }
  }
  return {
    left: PLACEMENT_ORIGIN_LEFT,
    top: PLACEMENT_ORIGIN_TOP,
    right: right + edgeBuffer,
    bottom: bottom + edgeBuffer,
  };
}

/** 分解と同じ viewport 拡張（Bridge/API のグループ外し配置用） */
export function computeUngroupPlacementViewports(
  pouch: Pouch,
  pouches: Pouch[],
  groups: Group[],
  formerGroup: Group,
  edgeBuffer = 0,
): { placementViewport: Viewport; searchViewport: Viewport } {
  const searchInsets = computeSplitGroupInsets(false);
  const placementViewport = expandPlacementViewportForSplit(
    computePlacementViewport(pouches, groups, edgeBuffer),
    pouch,
    1,
    searchInsets,
  );
  const searchViewport = expandViewportSearchExtents(
    placementViewport,
    pouch,
    1,
    searchInsets,
  );
  const siblingCount = (formerGroup.pouches ?? []).filter(id => id !== pouch.id).length;
  const isSoleMemberInGroup = (formerGroup.pouches ?? []).length === 1;
  const expandedSearchViewport =
    siblingCount > 0 || isSoleMemberInGroup
      ? expandViewportForSplitRelocate(searchViewport)
      : searchViewport;
  const formerBounds = getGroupPathBounds(formerGroup);
  const searchViewportFinal: Viewport = formerBounds
    ? {
        ...expandedSearchViewport,
        right: Math.max(expandedSearchViewport.right, formerBounds.right + 480),
        bottom: Math.max(expandedSearchViewport.bottom, formerBounds.bottom + 480),
      }
    : expandedSearchViewport;
  const wideRight = Math.max(
    placementViewport.right,
    searchViewportFinal.right,
    1400,
  );
  const wideBottom = Math.max(
    placementViewport.bottom,
    searchViewportFinal.bottom,
    1000,
  );
  const wide: Viewport = {
    left: placementViewport.left,
    top: placementViewport.top,
    right: wideRight,
    bottom: wideBottom,
  };
  return { placementViewport: wide, searchViewport: wide };
}

function ungroupPlacementOptions(
  pouch: Pouch,
  formerGroup: Group,
  pouches: Pouch[],
  groups: Group[],
): ClusterPlacementOptions {
  const siblingCount = (formerGroup.pouches ?? []).filter(id => id !== pouch.id).length;
  if (siblingCount === 0) {
    return {
      excludePouchId: pouch.id,
      excludeGroupIds: collectSoleMemberOverlapExcludeGroupIds(
        formerGroup.id,
        pouch,
        groups,
      ),
      mustAvoidGroupIds: [],
    };
  }
  return {
    excludePouchId: pouch.id,
    excludeGroupIds: [],
    mustAvoidGroupIds: [formerGroup.id],
  };
}

/**
 * グループから外すパウチの座標（分解と同じ最近傍空きスロット探索）。
 * group.pouches からは既に除いた後の groups を渡す。
 */
export function planUngroupPouchPosition(
  pouch: Pouch,
  formerGroup: Group,
  pouches: Pouch[],
  groups: Group[],
  edgeBuffer = 0,
): { x: number; y: number } | null {
  const { placementViewport, searchViewport } = computeUngroupPlacementViewports(
    pouch,
    pouches,
    groups,
    formerGroup,
    edgeBuffer,
  );
  const phaseOptions = ungroupPlacementOptions(pouch, formerGroup, pouches, groups);
  const hasTitle = !!(
    pouch.title?.trim() ||
    formerGroup.title?.trim()
  );
  const searchInsets = computeSplitGroupInsets(false);
  const groupOverlapInsets = computeSplitGroupInsets(hasTitle);

  const nearestCenter = findClusterNearestCenterSlot(
    1,
    pouch.x,
    pouch.y,
    pouches,
    groups,
    placementViewport,
    phaseOptions,
    true,
    searchInsets,
    searchViewport,
    groupOverlapInsets,
  );
  if (nearestCenter) {
    return computeBrickLayout(
      1,
      nearestCenter.centerX,
      nearestCenter.centerY,
      'center',
    )[0];
  }

  let topLeft = findClusterNearestTopLeftSlot(
    1,
    pouch.x,
    pouch.y,
    pouches,
    groups,
    placementViewport,
    phaseOptions,
    searchInsets,
    searchViewport,
    groupOverlapInsets,
  );
  if (!topLeft) {
    const mega = ensureSplitSearchViewportForCluster(
      placementViewport,
      searchViewport,
      1,
      groupOverlapInsets,
      {
        parentBounds: getGroupPathBounds(formerGroup),
        sourcePouch: pouch,
        mustAvoidGroupIds: phaseOptions.mustAvoidGroupIds,
        groups,
        pouches,
        extraMargin: SPLIT_RELOCATE_MAX_MARGIN,
      },
    );
    topLeft = findClusterNearestTopLeftSlot(
      1,
      pouch.x,
      pouch.y,
      pouches,
      groups,
      mega.placementViewport,
      phaseOptions,
      searchInsets,
      mega.searchViewport,
      groupOverlapInsets,
    );
  }
  if (topLeft) {
    return computeBrickLayout(1, topLeft.baseX, topLeft.baseY, 'topleft')[0];
  }
  return null;
}

export type PlaceNewPouchesMode = 'global' | 'group';

export interface PlaceNewPouchesParams {
  /** 配置するパウチ数 */
  count: number;
  pouches: Pouch[];
  groups: Group[];
  mode: PlaceNewPouchesMode;
  /** mode=group のとき必須 */
  groupId?: string;
  /** 新規グループで囲むときのタイトル（wrapWithGroup 時の padding 判定） */
  groupTitle?: string;
  /** 長いタイトル時の列間隔調整用（配置するパウチと同順） */
  pouchTitles?: (string | undefined)[];
  /** true: 新規 path bounds を返す（グループパディング込み） */
  wrapWithGroup?: boolean;
  /** group mode: 座標が既に確定しているメンバー id */
  fixedMemberIds?: string[];
  /** ×3 以上レプリカ用の下余白判定（配置するパウチと同順。未指定時は余白なし） */
  memberMultipliers?: number[];
  edgeBuffer?: number;
}

export interface PlaceNewPouchesResult {
  positions: { x: number; y: number }[];
  /** wrapWithGroup または group 拡張時の path bounds（タイトル余白込み） */
  groupBounds?: Rect;
  pouches: Pouch[];
  groups: Group[];
}

function findGroupLocalExtendSlot(
  n: number,
  group: Group,
  pouches: Pouch[],
  groups: Group[],
  placingIds: Set<string>,
  edgeBuffer = 0,
  layoutOptions?: BrickLayoutOptions,
  titleForInsets = '',
): { baseX: number; baseY: number } | null {
  const memberIds = new Set(group.pouches ?? []);
  const fixedPositions = pouches
    .filter(p => memberIds.has(p.id) && !placingIds.has(p.id))
    .map(p => ({ x: p.x, y: p.y }));

  const hasTitle = !!titleForInsets.trim() || !!group.title?.trim();
  const clusterAtOrigin = computeBrickLayout(n, 0, 0, 'topleft', layoutOptions);
  const clusterMemberWidth =
    computeClusterBounds(clusterAtOrigin, 0, 0).right -
    computeClusterBounds(clusterAtOrigin, 0, 0).left;
  const searchInsets = computeSplitGroupInsets(false);
  const groupOverlapInsets = computeSplitGroupInsets(
    hasTitle,
    titleForInsets.trim() || group.title?.trim(),
    clusterMemberWidth,
  );
  const placementViewport = computePlacementViewport(pouches, groups, edgeBuffer);
  const options: ClusterPlacementOptions = {
    excludeGroupIds: [group.id],
    pouchTitles: layoutOptions?.pouchTitles,
  };

  if (fixedPositions.length === 0) {
    return findClusterTopLeftSlot(
      n,
      pouches,
      groups,
      placementViewport,
      options,
      searchInsets,
      placementViewport,
      groupOverlapInsets,
    );
  }

  const memberBounds = computeClusterBounds(fixedPositions, 0, 0);
  const newClusterAtOrigin = computeClusterBounds(
    computeBrickLayout(n, 0, 0, 'topleft', layoutOptions),
    0,
    0,
  );
  const newH = newClusterAtOrigin.bottom - newClusterAtOrigin.top;
  const newW = newClusterAtOrigin.right - newClusterAtOrigin.left;

  const candidates = [
    { baseX: memberBounds.left, baseY: memberBounds.bottom + CLUSTER_BUFFER_Y },
    { baseX: memberBounds.right + CLUSTER_BUFFER_X, baseY: memberBounds.top },
    {
      baseX: memberBounds.left,
      baseY: memberBounds.top - CLUSTER_BUFFER_Y - newH,
    },
    {
      baseX: memberBounds.left - CLUSTER_BUFFER_X - newW,
      baseY: memberBounds.top,
    },
  ];

  for (const { baseX, baseY } of candidates) {
    const positions = computeBrickLayout(n, baseX, baseY, 'topleft', layoutOptions);
    const allPositions = [...fixedPositions, ...positions];
    if (
      checkSplitClusterPositions(
        allPositions,
        pouches,
        groups,
        placementViewport,
        options,
        searchInsets,
        groupOverlapInsets,
      ) === null
    ) {
      return { baseX, baseY };
    }
  }

  return null;
}

/**
 * 位置未定パウチ追加の統一入口（§2.1 モード A / §2.3 モード B）。
 * グループで囲むときは computeSplitGroupInsets のパディングを占有判定・path に反映する。
 */
export function placeNewPouches(
  params: PlaceNewPouchesParams,
): PlaceNewPouchesResult | null {
  const {
    count: n,
    pouches,
    groups,
    mode,
    groupId,
    groupTitle,
    pouchTitles,
    wrapWithGroup = false,
    fixedMemberIds = [],
    memberMultipliers = [],
    edgeBuffer = 0,
  } = params;

  if (n < 1) return null;

  const group =
    mode === 'group' ? groups.find(g => g.id === groupId) : undefined;
  const titleForInsets =
    groupTitle?.trim() || group?.title?.trim() || '';
  const hasTitle = !!titleForInsets;
  const replicaBottomPad = replicaSubtotalBottomPadPx(memberMultipliers);
  const layoutOptions: BrickLayoutOptions | undefined =
    pouchTitles?.length || memberMultipliers.length
      ? {
          pouchTitles,
          memberMultipliers: memberMultipliers.length
            ? memberMultipliers
            : undefined,
        }
      : undefined;
  const layoutAtOrigin = computeBrickLayout(n, 0, 0, 'topleft', layoutOptions);
  let clusterMemberWidth =
    computeClusterBounds(layoutAtOrigin, 0, 0).right -
    computeClusterBounds(layoutAtOrigin, 0, 0).left;
  // 1パウチでも2列分の幅でタイトル行数・insets を揃える（横並びの行ずれ防止）
  if (wrapWithGroup && n < 2) {
    const refLayout = computeBrickLayout(2, 0, 0, 'topleft', layoutOptions);
    const refWidth =
      computeClusterBounds(refLayout, 0, 0).right -
      computeClusterBounds(refLayout, 0, 0).left;
    clusterMemberWidth = Math.max(clusterMemberWidth, refWidth);
  }
  const searchInsets = computeSplitGroupInsets(false);
  const groupOverlapInsets =
    wrapWithGroup || mode === 'group'
      ? computeSplitGroupInsets(
          hasTitle,
          titleForInsets,
          clusterMemberWidth,
          replicaBottomPad,
        )
      : searchInsets;
  const clusterOptions: ClusterPlacementOptions | undefined = layoutOptions
    ? { pouchTitles: layoutOptions.pouchTitles }
    : undefined;

  const placementViewport = computePlacementViewport(pouches, groups, edgeBuffer);
  let slot: { baseX: number; baseY: number } | null = null;

  if (mode === 'global') {
    const { placementViewport: expandedPlacement, searchViewport } =
      ensureSplitSearchViewportForCluster(
        placementViewport,
        placementViewport,
        n,
        searchInsets,
        { pouches, groups },
      );
    slot = findClusterTopLeftSlot(
      n,
      pouches,
      groups,
      expandedPlacement,
      clusterOptions,
      searchInsets,
      searchViewport,
      groupOverlapInsets,
    );
  } else if (group) {
    const placingIds = new Set(
      (group.pouches ?? []).filter(id => !fixedMemberIds.includes(id)),
    );
    slot = findGroupLocalExtendSlot(
      n,
      group,
      pouches,
      groups,
      placingIds,
      edgeBuffer,
      layoutOptions,
      titleForInsets,
    );
  }

  if (!slot) return null;

  let positions = computeBrickLayout(
    n,
    slot.baseX,
    slot.baseY,
    'topleft',
    layoutOptions,
  );
  const pouchShiftY =
    wrapWithGroup || mode === 'group'
      ? computeGroupTitlePouchShiftY(titleForInsets, clusterMemberWidth)
      : 0;
  if (pouchShiftY > 0) {
    positions = positions.map(p => ({
      ...p,
      y: snapToGrid(p.y + pouchShiftY),
    }));
  }

  let resultPouches = pouches;
  let resultGroups = groups;
  let groupBounds: Rect | undefined;
  const displayInsets = computeSplitGroupInsets(
    hasTitle,
    titleForInsets,
    clusterMemberWidth,
    replicaBottomPad,
  );

  if (wrapWithGroup) {
    groupBounds = computeSplitGroupBounds(positions, displayInsets);
  } else if (mode === 'group' && group) {
    const fixedSet = new Set(fixedMemberIds);
    const fixedPositions = pouches
      .filter(p => (group.pouches ?? []).includes(p.id) && fixedSet.has(p.id))
      .map(p => ({ x: p.x, y: p.y }));
    const allPositions = [...fixedPositions, ...positions];
    groupBounds = computeSplitGroupBounds(allPositions, displayInsets);
    const expanded = expandGroupForClusterWithAncestors(
      group.id,
      groupBounds,
      resultPouches,
      resultGroups,
    );
    resultPouches = expanded.pouches;
    resultGroups = expanded.groups;
  }

  return {
    positions,
    groupBounds,
    pouches: resultPouches,
    groups: resultGroups,
  };
}

/**
 * Bridge/API write: グループ所属が外れたパウチを分解と同じ配置エンジンで再配置する。
 */
export function repositionPouchesRemovedFromGroups(
  previousGroups: Group[],
  pouches: Pouch[],
  groups: Group[],
  edgeBuffer = 0,
): Pouch[] {
  const removals: { pouchId: string; formerGroup: Group }[] = [];
  for (const group of groups) {
    const prev = previousGroups.find(g => g.id === group.id);
    if (!prev) continue;
    const nextSet = new Set(group.pouches ?? []);
    for (const id of prev.pouches ?? []) {
      if (!nextSet.has(id)) {
        removals.push({ pouchId: id, formerGroup: prev });
      }
    }
  }
  if (!removals.length) return pouches;

  const result = pouches.map(p => ({ ...p }));
  const byId = new Map(result.map(p => [p.id, p]));

  for (const { pouchId, formerGroup } of removals) {
    const pouch = byId.get(pouchId);
    if (!pouch || pouch.isGroupTotalPouch) continue;
    const pos = planUngroupPouchPosition(pouch, formerGroup, result, groups, edgeBuffer);
    if (pos) {
      pouch.x = pos.x;
      pouch.y = pos.y;
    }
  }
  return result;
}

/**
 * 分解と同じ配置計画（新規グループ一括作成用）。
 * 他メンバーはまだ存在しないものとして扱い、source 1 個の split と同じ探索を行う。
 */
export function planGroupMembersLikeSplit(
  source: Pouch,
  memberIds: string[],
  pouches: Pouch[],
  groups: Group[],
  groupTitle?: string,
): SplitPlacementPlan | null {
  const n = memberIds.length;
  if (n < 2) return null;

  const memberIdSet = new Set(memberIds);
  const placementPouches = pouches.filter(
    p => p.id === source.id || !memberIdSet.has(p.id),
  );
  const searchInsets = computeSplitGroupInsets(false);
  const placementViewport = expandPlacementViewportForSplit(
    computePlacementViewport(placementPouches, groups),
    source,
    n,
    searchInsets,
  );
  const searchViewport = expandViewportForSplitRelocate(placementViewport);
  const plan = planSplitPlacement(
    source,
    n,
    placementPouches,
    groups,
    placementViewport,
    searchViewport,
  );
  if (!plan) return null;

  const hasTitle = !!(groupTitle?.trim() || source.title?.trim());
  const bounds = computeSplitGroupBounds(
    plan.positions,
    computeSplitGroupInsets(hasTitle),
  );
  return { ...plan, splitGroupBounds: bounds };
}

function groupMemberIdsKey(ids: string[] | undefined): string {
  return [...(ids ?? [])].sort().join(',');
}

function shouldLayoutBridgeGroupLikeSplit(
  group: Group,
  previousGroups: Group[],
  previousPouchIds: Set<string>,
): boolean {
  const memberIds = group.pouches ?? [];
  if (memberIds.length < 2) return false;

  const prevGroup = previousGroups.find(g => g.id === group.id);
  if (!prevGroup) return true;

  const membersChanged =
    groupMemberIdsKey(prevGroup.pouches) !== groupMemberIdsKey(memberIds);
  const hasNewMember = memberIds.some(id => !previousPouchIds.has(id));
  return membersChanged || hasNewMember;
}

function applySplitPlanToGroup(
  group: Group,
  memberIds: string[],
  plan: SplitPlacementPlan,
  pouches: Pouch[],
  groups: Group[],
): { pouches: Pouch[]; groups: Group[] } {
  const positionById = new Map(
    memberIds.map((id, index) => [id, plan.positions[index]] as const),
  );
  const nextPouches = pouches.map(p => {
    const pos = positionById.get(p.id);
    return pos ? { ...p, x: pos.x, y: pos.y } : p;
  });
  const path = generateRoundedRectPath(
    plan.splitGroupBounds.left,
    plan.splitGroupBounds.top,
    plan.splitGroupBounds.right,
    plan.splitGroupBounds.bottom,
  );
  const nextGroups = groups.map(g =>
    g.id === group.id ? { ...g, path } : g,
  );
  return { pouches: nextPouches, groups: nextGroups };
}

/**
 * Bridge/API write: CLI でパウチ・グループが追加・変更されたとき、
 * 2個以上のメンバーを planGroupMembersLikeSplit（分解と同じ）で配置する。
 */
export function layoutBridgeGroupMembersLikeSplit(
  previousGroups: Group[],
  previousPouches: Pouch[],
  pouches: Pouch[],
  groups: Group[],
): { pouches: Pouch[]; groups: Group[] } {
  const previousPouchIds = new Set(previousPouches.map(p => p.id));
  const targets = groups.filter(g =>
    shouldLayoutBridgeGroupLikeSplit(g, previousGroups, previousPouchIds),
  );
  if (!targets.length) {
    return { pouches, groups };
  }

  let resultPouches = pouches.map(p => ({ ...p }));
  let resultGroups = groups.map(g => ({ ...g }));

  for (const group of targets) {
    const memberIds = group.pouches ?? [];
    const members = memberIds
      .map(id => resultPouches.find(p => p.id === id))
      .filter((p): p is Pouch => p != null);
    if (members.length < 2) continue;

    const source = members[0];
    const otherGroups = resultGroups.filter(g => g.id !== group.id);
    const plan = planGroupMembersLikeSplit(
      source,
      memberIds,
      resultPouches,
      otherGroups,
      group.title,
    );
    if (!plan) continue;

    const applied = applySplitPlanToGroup(
      group,
      memberIds,
      plan,
      resultPouches,
      resultGroups,
    );
    resultPouches = applied.pouches;
    resultGroups = applied.groups;
  }

  return { pouches: resultPouches, groups: resultGroups };
}

function computeMemberClusterInsets(
  group: Group,
  members: Pouch[],
): SplitGroupInsets {
  const positions = members.map(p => ({ x: p.x, y: p.y }));
  const title = group.title?.trim() || '';
  let clusterWidth =
    computeClusterBounds(positions, 0, 0).right -
    computeClusterBounds(positions, 0, 0).left;
  if (members.length < 2) {
    const refLayout = computeBrickLayout(2, 0, 0, 'topleft');
    const refWidth =
      computeClusterBounds(refLayout, 0, 0).right -
      computeClusterBounds(refLayout, 0, 0).left;
    clusterWidth = Math.max(clusterWidth, refWidth);
  }
  return computeSplitGroupInsets(
    !!title,
    title,
    clusterWidth,
    replicaSubtotalBottomPadPx(members.map(p => p.multiplier ?? 1)),
  );
}

function clusterOverlapsOtherGroupsIntersect(
  clusterBounds: Rect,
  groups: Group[],
  excludeGroupIds: string[],
): boolean {
  const excluded = new Set(excludeGroupIds);
  for (const g of groups) {
    if (excluded.has(g.id)) continue;
    const gb = getGroupPathBounds(g);
    if (gb && rectsIntersect(clusterBounds, gb)) {
      return true;
    }
  }
  return false;
}

function inflateRectByGridMargin(rect: Rect, margin = GRID_SIZE): Rect {
  return {
    left: rect.left - margin,
    top: rect.top - margin,
    right: rect.right + margin,
    bottom: rect.bottom + margin,
  };
}

/** 子グループ path が親の内側余白（最低1グリッド）を保っているか */
function nestedChildFitsInParentWithGridMargin(
  parent: Group,
  childId: string,
  groups: Group[],
): boolean {
  const parentBounds = getGroupPathBounds(parent);
  const child = groups.find(g => g.id === childId);
  const childBounds = child ? getGroupPathBounds(child) : null;
  if (!parentBounds || !childBounds) return true;
  return (
    childBounds.left >= parentBounds.left + GRID_SIZE &&
    childBounds.right <= parentBounds.right - GRID_SIZE &&
    childBounds.top >= parentBounds.top + GRID_SIZE &&
    childBounds.bottom <= parentBounds.bottom - GRID_SIZE
  );
}

function groupHasNestedChildNeedingParentMargin(
  group: Group,
  groups: Group[],
  previousGroups: Group[] = groups,
): boolean {
  for (const childId of nestedChildIdsForReconcile(group.id, groups, previousGroups)) {
    if (!nestedChildFitsInParentWithGridMargin(group, childId, groups)) {
      return true;
    }
  }
  return false;
}

function nestedChildIdsForReconcile(
  groupId: string,
  groups: Group[],
  previousGroups: Group[] = groups,
): string[] {
  const ids = new Set([
    ...groupsPhysicallyNestedInside(groupId, previousGroups),
    ...groupsPhysicallyNestedInside(groupId, groups),
  ]);
  return Array.from(ids);
}

/**
 * reconcile 用の理想 bounds（衝突チェックなし・子グループ union・縮小なし）。
 */
function computeIdealReconcileBounds(
  group: Group,
  pouches: Pouch[],
  groups: Group[],
  previousGroups: Group[] = groups,
): Rect | null {
  const current = getGroupPathBounds(group);
  const members = (group.pouches ?? [])
    .map(id => pouches.find(p => p.id === id))
    .filter((p): p is Pouch => p != null && !p._isDeleting);

  let ideal: Rect | null = current ? { ...current } : null;

  if (members.length) {
    const positions = members.map(p => ({ x: p.x, y: p.y }));
    const insets = computeMemberClusterInsets(group, members);
    const memberBounds = computeClusterBounds(positions, 0, 0);

    if (current) {
      ideal = {
        left: Math.min(current.left, memberBounds.left - insets.left),
        top: Math.min(current.top, memberBounds.top - insets.top),
        right: Math.max(current.right, memberBounds.right + insets.right),
        bottom: Math.max(current.bottom, memberBounds.bottom + insets.bottom),
      };
    } else {
      ideal = computeSplitGroupBounds(positions, insets);
    }
  }

  for (const childId of nestedChildIdsForReconcile(group.id, groups, previousGroups)) {
    const child = groups.find(g => g.id === childId);
    const childBounds = child ? getGroupPathBounds(child) : null;
    if (childBounds) {
      const padded = inflateRectByGridMargin(childBounds);
      ideal = ideal ? unionRects(ideal, padded) : { ...padded };
    }
  }

  if (current && ideal) {
    ideal = unionRects(current, ideal);
    ideal.left = Math.max(ideal.left, current.left, PLACEMENT_ORIGIN_LEFT);
    ideal.top = Math.max(ideal.top, current.top, PLACEMENT_ORIGIN_TOP);
  }

  return ideal ?? current;
}

/** @deprecated 互換 alias */
function computeReconcileExpandBounds(
  group: Group,
  pouches: Pouch[],
  groups: Group[],
): Rect | null {
  return computeIdealReconcileBounds(group, pouches, groups);
}

function groupsToMoveAsBlock(groupId: string, groups: Group[]): string[] {
  return [groupId, ...groupsPhysicallyNestedInside(groupId, groups)];
}

/** 押し出し対象が子だけ当たっても、除外外の最外祖先を塊ルートにする */
function outermostForeignDisplaceRoot(
  groupId: string,
  excludeIds: Set<string>,
  groups: Group[],
): string {
  const candidates = new Set<string>([groupId]);
  for (const ancestorId of groupsPhysicallyContaining(groupId, groups)) {
    if (!excludeIds.has(ancestorId)) {
      candidates.add(ancestorId);
    }
  }

  const candidateIds = Array.from(candidates);
  const roots = candidateIds.filter(
    id =>
      !candidateIds.some(
        other =>
          other !== id &&
          isGroupPhysicallyContained(id, other, groups),
      ),
  );

  const containingRoots = roots.filter(
    id => id === groupId || isGroupPhysicallyContained(groupId, id, groups),
  );
  if (containingRoots.length <= 1) {
    return containingRoots[0] ?? groupId;
  }

  return containingRoots.reduce((best, id) => {
    const bounds = getGroupPathBounds(groups.find(g => g.id === id)!);
    const bestBounds = getGroupPathBounds(groups.find(g => g.id === best)!);
    if (!bounds || !bestBounds) return best;
    const area =
      (bounds.right - bounds.left) * (bounds.bottom - bounds.top);
    const bestArea =
      (bestBounds.right - bestBounds.left) * (bestBounds.bottom - bestBounds.top);
    return area > bestArea ? id : best;
  });
}

function collectReconcileAncestorExcludeIds(
  groupId: string,
  previousGroups: Group[],
): Set<string> {
  const ids = new Set<string>();
  for (const ancestorId of groupsPhysicallyContaining(groupId, previousGroups)) {
    ids.add(ancestorId);
  }
  let currentId = groupId;
  for (let depth = 0; depth < 32; depth++) {
    const container = findInnermostContainerGroup(currentId, previousGroups);
    if (!container) break;
    ids.add(container.id);
    currentId = container.id;
  }
  return ids;
}

function displaceOverlappingExcludingSubtree(
  deltaX: number,
  deltaY: number,
  rootGroupId: string,
  affectedRegion: Rect,
  pouches: Pouch[],
  groups: Group[],
  maxIterations = 50,
  followRegion = true,
  alreadyDisplacedBlocksExternal?: Set<string>,
  expandIdealBounds?: Rect | null,
  expandFromBounds?: Rect | null,
  nestedExemptFromPush?: Set<string>,
  additionalExcludeIds?: Set<string>,
): { pouches: Pouch[]; groups: Group[] } {
  if (deltaX === 0 && deltaY === 0) {
    return { pouches, groups };
  }

  const subtreeGroupIds = [
    rootGroupId,
    ...groupsPhysicallyNestedInside(rootGroupId, groups),
  ];
  const pouchInExpandingSubtree = (pouchId: string, gs: Group[]): boolean =>
    subtreeGroupIds.some(gid => {
      const g = gs.find(x => x.id === gid);
      return (g?.pouches ?? []).includes(pouchId);
    });

  const excludeIds = new Set([
    ...subtreeGroupIds,
    ...groupsPhysicallyContaining(rootGroupId, groups),
    ...(additionalExcludeIds ? Array.from(additionalExcludeIds) : []),
  ]);

  let currentPouches = pouches.map(p => ({ ...p }));
  let currentGroups = groups.map(g => ({ ...g }));
  const staticRegion = { ...affectedRegion };
  let region = { ...affectedRegion };
  const alreadyDisplacedBlocks =
    alreadyDisplacedBlocksExternal ?? new Set<string>();

  for (let iter = 0; iter < maxIterations; iter++) {
    let moved = false;
    const nextPouches = currentPouches.map(p => ({ ...p }));
    const nextGroups = currentGroups.map(g => ({ ...g }));
    const groupsMovingThisPass = new Set<string>();

    for (let gi = 0; gi < nextGroups.length; gi++) {
      const g = nextGroups[gi];
      if (excludeIds.has(g.id) || nestedExemptFromPush?.has(g.id)) continue;
      const gb = getPathBounds(g.path) ?? getGroupPathBounds(g);
      if (!gb || !rectsIntersect(gb, region)) continue;
      // 成長リングと重なる外側グループだけ押す。旧条件 (left < oldRight) だと
      // 成長帯に食い込む兄弟 (gray 等) が段階拡大の2段目以降で誤スキップされる。
      if (expandFromBounds) {
        if (deltaX > 0 && gb.right <= expandFromBounds.right) continue;
        if (deltaY > 0 && gb.bottom <= expandFromBounds.bottom) continue;
        if (deltaX < 0 && gb.left >= expandFromBounds.left) continue;
        if (deltaY < 0 && gb.top >= expandFromBounds.top) continue;
      }

      const blockRoot = outermostForeignDisplaceRoot(
        g.id,
        excludeIds,
        currentGroups,
      );
      const blockIds = groupsToMoveAsBlock(blockRoot, currentGroups);
      if (blockIds.some(id => alreadyDisplacedBlocks.has(id))) continue;
      if (blockIds.some(id => groupsMovingThisPass.has(id))) continue;

      for (const blockId of blockIds) {
        groupsMovingThisPass.add(blockId);
        alreadyDisplacedBlocks.add(blockId);
        const blockIdx = nextGroups.findIndex(x => x.id === blockId);
        if (blockIdx < 0) continue;
        nextGroups[blockIdx] = {
          ...nextGroups[blockIdx],
          path: translatePath(nextGroups[blockIdx].path, deltaX, deltaY),
        };
      }

      const blockMemberIds = pouchIdsInDisplacedBlock(blockIds, currentGroups);
      for (let i = 0; i < nextPouches.length; i++) {
        const p = nextPouches[i];
        if (blockMemberIds.has(p.id)) {
          nextPouches[i] = { ...p, x: p.x + deltaX, y: p.y + deltaY };
        } else if (
          p.isGroupTotalPouch &&
          p.referencedGroupId &&
          blockIds.includes(p.referencedGroupId)
        ) {
          nextPouches[i] = { ...p, x: p.x + deltaX, y: p.y + deltaY };
        }
      }
      moved = true;
    }

    for (let i = 0; i < nextPouches.length; i++) {
      const p = nextPouches[i];
      if (pouchInExpandingSubtree(p.id, currentGroups)) continue;
      const expandingRoot = groups.find(g => g.id === rootGroupId);
      if (
        expandingRoot &&
        (expandingRoot.pouches ?? []).includes(p.id) &&
        pouchCenterInGroupBounds(expandingRoot, p.x, p.y)
      ) {
        continue;
      }
      if (p.isGroupTotalPouch) {
        if (
          p.referencedGroupId &&
          groupsMovingThisPass.has(p.referencedGroupId)
        ) {
          continue;
        }
        continue;
      }
      const owner = findGroupForPouchId(p.id, currentGroups, currentPouches);
      if (owner && excludeIds.has(owner.id)) continue;
      if (owner && nestedExemptFromPush?.has(owner.id)) continue;
      if (owner && groupsMovingThisPass.has(owner.id)) continue;
      if (owner) {
        const ob = getGroupPathBounds(owner);
        if (ob && rectsIntersect(ob, region)) continue;
      }

      if (rectsIntersect(pouchCircleBounds(p), region)) {
        nextPouches[i] = { ...p, x: p.x + deltaX, y: p.y + deltaY };
        moved = true;
      }
    }

    if (!moved) break;
    currentPouches = nextPouches;
    currentGroups = nextGroups;
    if (followRegion) {
      region = translateRect(region, deltaX, deltaY);
    } else {
      region = { ...staticRegion };
    }
  }

  return { pouches: currentPouches, groups: currentGroups };
}

/** reconcile 用: 成長リング固定のまま連鎖押し出し（リング追従は暴走するため無効） */
function displaceOverlappingCascadeExcludingSubtree(
  deltaX: number,
  deltaY: number,
  rootGroupId: string,
  affectedRegion: Rect,
  pouches: Pouch[],
  groups: Group[],
): { pouches: Pouch[]; groups: Group[] } {
  return displaceOverlappingExcludingSubtree(
    deltaX,
    deltaY,
    rootGroupId,
    affectedRegion,
    pouches,
    groups,
    20,
    false,
  );
}

function displaceOverlappingOnceExcludingSubtree(
  deltaX: number,
  deltaY: number,
  rootGroupId: string,
  affectedRegion: Rect,
  pouches: Pouch[],
  groups: Group[],
): { pouches: Pouch[]; groups: Group[] } {
  return displaceOverlappingOnceExcludingSubtreeTracked(
    deltaX,
    deltaY,
    rootGroupId,
    affectedRegion,
    pouches,
    groups,
    new Set<string>(),
  );
}

function displaceOverlappingOnceExcludingSubtreeTracked(
  deltaX: number,
  deltaY: number,
  rootGroupId: string,
  affectedRegion: Rect,
  pouches: Pouch[],
  groups: Group[],
  alreadyDisplacedBlocks: Set<string>,
  expandIdealBounds?: Rect | null,
  expandFromBounds?: Rect | null,
  nestedExemptFromPush?: Set<string>,
  additionalExcludeIds?: Set<string>,
): { pouches: Pouch[]; groups: Group[] } {
  return displaceOverlappingExcludingSubtree(
    deltaX,
    deltaY,
    rootGroupId,
    affectedRegion,
    pouches,
    groups,
    1,
    true,
    alreadyDisplacedBlocks,
    expandIdealBounds,
    expandFromBounds,
    nestedExemptFromPush,
    additionalExcludeIds,
  );
}

/** reconcile: path 拡大＋成長リングだけ押し出し（内包する子グループは連れない） */
function reconcileExpandWithPush(
  groupId: string,
  idealBounds: Rect,
  pouches: Pouch[],
  groups: Group[],
  previousGroups: Group[],
): { pouches: Pouch[]; groups: Group[] } {
  const group = groups.find(g => g.id === groupId);
  const oldBounds = group ? getGroupPathBounds(group) : null;
  if (!oldBounds) return { pouches, groups };

  const newBounds = unionRects(oldBounds, idealBounds);
  if (!boundsNeedsGrow(oldBounds, newBounds)) {
    return { pouches, groups };
  }

  // 祖先拡大時に、移動前から内包していた子を二重押し出ししない
  const nestedExemptFromPush = new Set(
    groupsPhysicallyNestedInside(groupId, previousGroups),
  );
  // 拡大で子が親 bounds からはみ出しても、reconcile 開始前の祖先は押し出し対象にしない
  const reconcileAncestorExcludeIds = collectReconcileAncestorExcludeIds(
    groupId,
    previousGroups,
  );

  let resultPouches = pouches.map(p => ({ ...p }));
  let resultGroups = groups.map(g => ({ ...g }));
  let workingBounds = { ...oldBounds };
  const displacedBlocksInReconcile = new Set<string>();

  const growEdge = (edge: 'left' | 'top' | 'right' | 'bottom', target: number) => {
    const currentVal = workingBounds[edge];
    if (currentVal === target) return;
    const nextBounds = { ...workingBounds, [edge]: target };
    let deltaX = 0;
    let deltaY = 0;
    if (edge === 'bottom') deltaY = target - currentVal;
    if (edge === 'right') deltaX = target - currentVal;
    if (edge === 'top') deltaY = target - currentVal;
    if (edge === 'left') deltaX = target - currentVal;

    for (const region of expansionGrowthRegions(workingBounds, nextBounds)) {
      if (deltaX === 0 && deltaY === 0) continue;
      const displaced = displaceOverlappingOnceExcludingSubtreeTracked(
        deltaX,
        deltaY,
        groupId,
        region,
        resultPouches,
        resultGroups,
        displacedBlocksInReconcile,
        newBounds,
        workingBounds,
        nestedExemptFromPush,
        reconcileAncestorExcludeIds,
      );
      resultPouches = displaced.pouches;
      resultGroups = displaced.groups;
    }
    workingBounds = nextBounds;
    resultGroups = setGroupPathFromBounds(groupId, workingBounds, resultGroups);
  };

  growEdge('bottom', newBounds.bottom);
  growEdge('right', newBounds.right);
  // reconcile では左/上への拡大は行わない（insets 過大で左押し出し暴走するため）
  // growEdge('top', newBounds.top);
  // growEdge('left', newBounds.left);

  const finalBounds = {
    left: oldBounds.left,
    top: oldBounds.top,
    right: workingBounds.right,
    bottom: workingBounds.bottom,
  };
  resultGroups = setGroupPathFromBounds(groupId, finalBounds, resultGroups);
  return propagateAncestorReconcileWithPush(
    groupId,
    resultPouches,
    resultGroups,
    previousGroups,
  );
}

/** 子の拡大後、祖先が子を覆えなくなったら push 付きで拡大（緑など外側障害物も押す） */
function propagateAncestorReconcileWithPush(
  childGroupId: string,
  pouches: Pouch[],
  groups: Group[],
  previousGroups: Group[],
): { pouches: Pouch[]; groups: Group[] } {
  let resultPouches = pouches.map(p => ({ ...p }));
  let resultGroups = groups.map(g => ({ ...g }));
  let currentChildId = childGroupId;

  for (let depth = 0; depth < groups.length; depth++) {
    const container = findInnermostContainerGroup(currentChildId, resultGroups);
    if (!container) break;

    const ideal = computeIdealReconcileBounds(
      container,
      resultPouches,
      resultGroups,
      previousGroups,
    );
    const current = getGroupPathBounds(container);
    if (!ideal || !current || !boundsNeedsGrow(current, ideal)) {
      break;
    }

    const pushed = reconcileExpandWithPush(
      container.id,
      ideal,
      resultPouches,
      resultGroups,
      previousGroups,
    );
    resultPouches = pushed.pouches;
    resultGroups = pushed.groups;
    currentChildId = container.id;
  }

  return { pouches: resultPouches, groups: resultGroups };
}

function computeMemberClusterBoundsForGroup(
  group: Group,
  pouches: Pouch[],
): Rect | null {
  const members = (group.pouches ?? [])
    .map(id => pouches.find(p => p.id === id))
    .filter((p): p is Pouch => p != null && !p._isDeleting);
  if (!members.length) return null;

  const positions = members.map(p => ({ x: p.x, y: p.y }));
  const insets = computeMemberClusterInsets(group, members);
  return computeSplitGroupBounds(positions, insets);
}

function boundsNeedsGrow(current: Rect, needed: Rect, slack = 1): boolean {
  return (
    needed.left < current.left - slack ||
    needed.top < current.top - slack ||
    needed.right > current.right + slack ||
    needed.bottom > current.bottom + slack
  );
}

function expansionGrowthRegions(oldBounds: Rect, newBounds: Rect): Rect[] {
  const regions: Rect[] = [];
  if (newBounds.left < oldBounds.left) {
    regions.push({
      left: newBounds.left,
      top: Math.min(newBounds.top, oldBounds.top),
      right: oldBounds.left,
      bottom: Math.max(newBounds.bottom, oldBounds.bottom),
    });
  }
  if (newBounds.right > oldBounds.right) {
    regions.push({
      left: oldBounds.right,
      top: Math.min(newBounds.top, oldBounds.top),
      right: newBounds.right,
      bottom: Math.max(newBounds.bottom, oldBounds.bottom),
    });
  }
  if (newBounds.top < oldBounds.top) {
    regions.push({
      left: newBounds.left,
      top: newBounds.top,
      right: newBounds.right,
      bottom: oldBounds.top,
    });
  }
  if (newBounds.bottom > oldBounds.bottom) {
    regions.push({
      left: newBounds.left,
      top: oldBounds.bottom,
      right: newBounds.right,
      bottom: newBounds.bottom,
    });
  }
  return regions;
}

function pouchCenterInGroupBounds(group: Group, x: number, y: number): boolean {
  const bounds = getGroupPathBounds(group);
  if (!bounds) return false;
  return (
    x >= bounds.left &&
    x <= bounds.right &&
    y >= bounds.top &&
    y <= bounds.bottom
  );
}

/** パウチ円（中心±半径）が path 内に収まるか */
function pouchCircleFitsInGroupBounds(group: Group, x: number, y: number): boolean {
  const bounds = getGroupPathBounds(group);
  if (!bounds) return false;
  const circle = pouchCircleBounds({ x, y });
  return (
    circle.left >= bounds.left &&
    circle.top >= bounds.top &&
    circle.right <= bounds.right &&
    circle.bottom <= bounds.bottom
  );
}

function groupHasOverflowingMember(group: Group, pouches: Pouch[]): boolean {
  const memberIds = new Set(group.pouches ?? []);
  for (const pouch of pouches) {
    if (!memberIds.has(pouch.id)) continue;
    if (pouch._isDeleting || pouch.isGroupTotalPouch) continue;
    if (typeof pouch.x !== 'number' || typeof pouch.y !== 'number') continue;
    if (!pouchCircleFitsInGroupBounds(group, pouch.x, pouch.y)) return true;
  }
  return false;
}

/**
 * API write で座標だけ変えたパウチを reconcile 前に group.pouches へ仮登録する。
 * 未登録のままだと groupGainedMemberByPlacement が見逃し、はみ出し拡大が走らない。
 */
export function enrollMovedPouchesForReconcile(
  previousPouches: Pouch[],
  previousGroups: Group[],
  pouches: Pouch[],
  groups: Group[],
): Group[] {
  let resultGroups = groups.map(g => ({
    ...g,
    pouches: [...(g.pouches ?? [])],
  }));

  for (const pouch of pouches) {
    if (pouch._isDeleting || pouch.isGroupTotalPouch) continue;
    if (typeof pouch.x !== 'number' || typeof pouch.y !== 'number') continue;

    const prev = previousPouches.find(p => p.id === pouch.id);
    if (prev?.x === pouch.x && prev?.y === pouch.y) continue;

    for (const group of resultGroups) {
      if ((group.pouches ?? []).includes(pouch.id)) continue;

      const wasMember = previousGroups
        .find(g => g.id === group.id)
        ?.pouches?.includes(pouch.id);

      const inside = pouchCenterInGroupBounds(group, pouch.x, pouch.y);
      let nearOverflow = false;
      if (!inside) {
        const bounds = getGroupPathBounds(group);
        if (bounds) {
          const margin = POUCH_DIAMETER;
          const nearEnvelope =
            pouch.x >= bounds.left - margin &&
            pouch.x <= bounds.right + margin &&
            pouch.y >= bounds.top - margin &&
            pouch.y <= bounds.bottom + margin;
          const siblingAlreadyContains = resultGroups.some(
            other =>
              other.id !== group.id &&
              !isGroupPhysicallyContained(other.id, group.id, resultGroups) &&
              !isGroupPhysicallyContained(group.id, other.id, resultGroups) &&
              pouchCenterInGroupBounds(other, pouch.x, pouch.y),
          );
          nearOverflow =
            nearEnvelope &&
            !siblingAlreadyContains &&
            resultGroups.some(
              outer =>
                outer.id !== group.id &&
                pouchCenterInGroupBounds(outer, pouch.x, pouch.y) &&
                isGroupPhysicallyContained(group.id, outer.id, resultGroups),
            );
        }
      }

      if (wasMember || inside || nearOverflow) {
        resultGroups = resultGroups.map(g =>
          g.id === group.id
            ? {
                ...g,
                pouches: Array.from(
                  new Set([...(g.pouches ?? []), pouch.id]),
                ),
              }
            : g,
        );
      }
    }
  }

  return resultGroups;
}

/** パウチが今回の更新でグループの reconcile 対象になったか */
function groupGainedMemberByPlacement(
  group: Group,
  previousPouches: Pouch[],
  pouches: Pouch[],
  groups: Group[],
): boolean {
  const memberIds = new Set(group.pouches ?? []);
  for (const pouch of pouches) {
    if (!memberIds.has(pouch.id)) continue;
    if (pouch._isDeleting || pouch.isGroupTotalPouch) continue;
    if (typeof pouch.x !== 'number' || typeof pouch.y !== 'number') continue;

    const isInside = pouchCenterInGroupBounds(group, pouch.x, pouch.y);
    if (!isInside) return true;

    const primaryId = findPrimaryGroupIdForPouchCenter(
      pouch.x,
      pouch.y,
      groups,
    );
    if (primaryId != null && primaryId !== group.id) continue;

    const prev = previousPouches.find(p => p.id === pouch.id);
    const wasInside = prev
      ? pouchCenterInGroupBounds(group, prev.x, prev.y)
      : false;
    if (!wasInside) return true;
  }
  return false;
}

function groupsPhysicallyNestedInside(groupId: string, groups: Group[]): string[] {
  return groups
    .filter(
      g => g.id !== groupId && isGroupPhysicallyContained(g.id, groupId, groups),
    )
    .map(g => g.id);
}

function hasPhysicallyNestedChildGroups(groupId: string, groups: Group[]): boolean {
  return groupsPhysicallyNestedInside(groupId, groups).length > 0;
}

function groupsInNestedSubtree(rootGroupId: string, groups: Group[]): string[] {
  return [rootGroupId, ...groupsPhysicallyNestedInside(rootGroupId, groups)];
}

function translateGroupNestedSubtree(
  rootGroupId: string,
  deltaX: number,
  deltaY: number,
  pouches: Pouch[],
  groups: Group[],
): { pouches: Pouch[]; groups: Group[] } {
  if (deltaX === 0 && deltaY === 0) {
    return { pouches, groups };
  }
  const subtreeIds = groupsInNestedSubtree(rootGroupId, groups);
  const subtreeIdSet = new Set(subtreeIds);
  const memberIds = new Set<string>();
  for (const gid of subtreeIds) {
    const g = groups.find(x => x.id === gid);
    for (const pid of g?.pouches ?? []) {
      memberIds.add(pid);
    }
  }
  const updatedGroups = groups.map(g =>
    subtreeIdSet.has(g.id)
      ? { ...g, path: translatePath(g.path, deltaX, deltaY) }
      : g,
  );
  const updatedPouches = pouches.map(p => {
    if (memberIds.has(p.id)) {
      return { ...p, x: p.x + deltaX, y: p.y + deltaY };
    }
    if (
      p.isGroupTotalPouch &&
      p.referencedGroupId &&
      subtreeIdSet.has(p.referencedGroupId)
    ) {
      return { ...p, x: p.x + deltaX, y: p.y + deltaY };
    }
    return p;
  });
  return { pouches: updatedPouches, groups: updatedGroups };
}

function collectReconcileRelocateExcludeIds(
  groupId: string,
  source: Pouch,
  groups: Group[],
): string[] {
  const container = findInnermostContainerGroup(groupId, groups);
  const base = container
    ? collectSoleMemberOverlapExcludeGroupIds(container.id, source, groups)
    : groupOverlapExcludeIds(groupId, groups);
  return Array.from(
    new Set([...base, ...groupsInNestedSubtree(groupId, groups)]),
  );
}

function computeReconcileBlockBounds(
  group: Group,
  needed: Rect,
  groups: Group[],
): Rect {
  let blockBounds = { ...needed };
  for (const gid of groupsInNestedSubtree(group.id, groups)) {
    if (gid === group.id) continue;
    const g = groups.find(x => x.id === gid);
    const gb = g ? getGroupPathBounds(g) : null;
    if (gb) {
      blockBounds = unionRects(blockBounds, gb);
    }
  }
  return blockBounds;
}

function resolveReconcileSearchViewport(
  group: Group,
  blockBounds: Rect,
  pouches: Pouch[],
  groups: Group[],
): Viewport {
  const container = findInnermostContainerGroup(group.id, groups);
  const containerBounds = container ? getGroupPathBounds(container) : null;
  if (container && containerBounds) {
    const envelope = resolveSoleMemberPlacementEnvelope(
      container.id,
      containerBounds,
      groups,
    );
    return ensureViewportFitsRect(
      expandViewportForSplitRelocate({
        left: envelope.left,
        top: envelope.top,
        right: envelope.right,
        bottom: envelope.bottom,
      }),
      blockBounds,
    );
  }
  return ensureViewportFitsRect(
    expandViewportForSplitRelocate(computePlacementViewport(pouches, groups)),
    blockBounds,
  );
}

/**
 * ネスト対応の塊移動: 子グループ path・所属パウチもまとめて移し、親 envelope 内で空きを探す。
 */
function relocateGroupBlockForReconcile(
  group: Group,
  needed: Rect,
  pouches: Pouch[],
  groups: Group[],
): { pouches: Pouch[]; groups: Group[] } | null {
  const memberIds = group.pouches ?? [];
  if (memberIds.length < 2) return null;

  const source = pouches.find(p => p.id === memberIds[0]);
  if (!source) return null;

  const blockBounds = computeReconcileBlockBounds(group, needed, groups);
  const excludeIds = collectReconcileRelocateExcludeIds(
    group.id,
    source,
    groups,
  );
  const searchViewport = resolveReconcileSearchViewport(
    group,
    blockBounds,
    pouches,
    groups,
  );
  const maxMargin = maxRelocateMargin(memberIds.length, blockBounds);

  const slot = findClearRectSlot(
    blockBounds,
    searchViewport,
    pouches,
    groups,
    excludeIds,
    '',
    blockBounds,
    maxMargin,
  );

  if (!slot) {
    return null;
  }

  const deltaX = snapToGrid(slot.left - blockBounds.left);
  const deltaY = snapToGrid(slot.top - blockBounds.top);
  let moved = translateGroupNestedSubtree(
    group.id,
    deltaX,
    deltaY,
    pouches,
    groups,
  );

  const translatedNeeded = translateRect(needed, deltaX, deltaY);
  moved.groups = setGroupPathFromBounds(group.id, translatedNeeded, moved.groups);
  moved.groups = expandGroupBoundsOnlyWithAncestors(
    group.id,
    translatedNeeded,
    moved.groups,
  );
  return moved;
}

function groupOverlapExcludeIds(groupId: string, groups: Group[]): string[] {
  const group = groups.find(g => g.id === groupId);
  const bounds = group ? getGroupPathBounds(group) : null;
  const excluded = new Set<string>([
    groupId,
    ...groupsPhysicallyNestedInside(groupId, groups),
  ]);
  if (!bounds) return Array.from(excluded);

  for (const other of groups) {
    if (excluded.has(other.id)) continue;
    const ob = getGroupPathBounds(other);
    if (!ob) continue;
    if (rectFullyContains(ob, bounds)) {
      excluded.add(other.id);
    }
  }
  return Array.from(excluded);
}

function expansionCollidesWithOthers(
  oldBounds: Rect,
  newBounds: Rect,
  groupId: string,
  pouches: Pouch[],
  groups: Group[],
): boolean {
  const exclude = groupOverlapExcludeIds(groupId, groups);
  const growthRegions = expansionGrowthRegions(oldBounds, newBounds);
  for (const region of growthRegions) {
    if (clusterOverlapsOtherGroupsIntersect(region, groups, exclude)) {
      return true;
    }
  }

  for (const pouch of pouches) {
    if (pouch.isGroupTotalPouch) continue;
    if (!isForeignSplitPouch(pouch, groups, exclude, '')) continue;
    const pb = pouchCircleBounds(pouch);
    if (!rectsIntersect(pb, newBounds)) continue;
    if (rectsIntersect(pb, oldBounds)) continue;
    return true;
  }
  return false;
}

/**
 * 所属同期後: メンバー全員を覆うようグループ path を拡大する。
 * 拡大が他グループ等と衝突する場合は分解配置と同様に塊ごと近傍へ移動する。
 */
export function reconcileGroupsToFitMembers(
  previousPouches: Pouch[],
  pouches: Pouch[],
  groups: Group[],
  previousGroups: Group[] = groups,
): { pouches: Pouch[]; groups: Group[] } {
  let resultPouches = pouches.map(p => ({ ...p }));
  let resultGroups = groups.map(g => ({ ...g }));
  const reconcileGroupIds = resultGroups.map(g => g.id);

  for (const groupId of reconcileGroupIds) {
    const group = resultGroups.find(g => g.id === groupId);
    if (!group) continue;
    const memberIds = group.pouches ?? [];
    if (!memberIds.length) continue;

    if (!groupGainedMemberByPlacement(group, previousPouches, resultPouches, resultGroups)) {
      if (!groupHasNestedChildNeedingParentMargin(group, resultGroups, previousGroups)) {
        continue;
      }
    }

    // はみ出しメンバーがいないときは path を触らない（所属は sync で足りる）
    if (
      !groupHasOverflowingMember(group, resultPouches) &&
      !groupHasNestedChildNeedingParentMargin(group, resultGroups, previousGroups)
    ) {
      continue;
    }

    const ideal = computeIdealReconcileBounds(
      group,
      resultPouches,
      resultGroups,
      previousGroups,
    );
    const current = getGroupPathBounds(group);
    if (!ideal || !current || !boundsNeedsGrow(current, ideal)) {
      continue;
    }

    const pushed = reconcileExpandWithPush(
      group.id,
      ideal,
      resultPouches,
      resultGroups,
      previousGroups,
    );
    resultPouches = pushed.pouches;
    resultGroups = pushed.groups;
  }

  return { pouches: resultPouches, groups: resultGroups };
}

/** @deprecated layoutBridgeGroupMembersLikeSplit を使用 */
export function layoutNewGroupsLikeSplit(
  previousGroups: Group[],
  pouches: Pouch[],
  groups: Group[],
): { pouches: Pouch[]; groups: Group[] } {
  return layoutBridgeGroupMembersLikeSplit(
    previousGroups,
    [],
    pouches,
    groups,
  );
}
